/**
 * BTA Multi-Booking - Main Controller (Event-Driven)
 * Combines: Main Controller + Highlight + Automation + Date Removal
 * With integrated diagnostic testing
 * 
 * Version 23: Flex modal fix (Oct 31 2026): on phones the card was capped at 94vh with
 *   overflow hidden, so Confirm was cut off on shorter screens (iPhone SE/16,
 *   Galaxy A55). The card is now sized to the visible screen (visualViewport),
 *   the content scrolls, and Booking total + Confirm sit in a pinned footer
 *   that is always on screen.
 * Version 22: BTA widget rebuild compatibility (Sept 2026)
 *             Timeslot input is now id="bta-product-widget-pickup-time-select-N"
 *             (was id*="react-select") — added TIMESLOT_INPUT_SELECTOR fallback chain
 *             Price element is now .price-container span.ml10 (was span.hXgXeP)
 *             Added structural CSS rules for minus buttons / booking list (.booking-times)
 *             All previous selectors retained as fallbacks
 * Version 21: Added in-widget waitlist button
 *             injectWaitlistButton() — outlined gold-bordered button below price
 *             Always visible inside the BTA widget for daycare
 *             Uses WAITLIST_URL = https://kennelclubboarding.ca/pages/daycare-waitlist-1
 *             Idempotent (skips if #bta-waitlist-button already present)
 * Version 18: Added multi-day pass discount counter (inside widget, above calendar)
 *             Removed multi-date welcome notification modal
 *             Fixed date deselection - updated minus button detection using SVG path analysis
 * Version 20: Added Flex Booking Protection modal and line property injection
 *             FLEX_CONFIG, flexState, getBookingAmountFromIframe, showFlexModal
 *             Fetch interceptor shows modal on first cart add per session
 *             Injects _flexProtection and Booking Protection line properties
 * Version 19: Added real-time availability badge (below calendar, above price)
 */

(function() {
  'use strict';

  // console.log('%c[BTA Main] Event-Driven Controller Loading...', 'color: #3b82f6; font-weight: bold');

  // ============================================================================
  // BTA DOM SELECTORS (v22) — fallback chains, most specific/current first
  // BTA rebuilds its widget periodically; hashed styled-components classes change.
  // ============================================================================
  const TIMESLOT_INPUT_SELECTOR = [
    'input[id*="time-select"]',              // Sept 2026 build: bta-product-widget-pickup-time-select-N
    'input[id*="react-select"]',             // Pre-Sept 2026 build
    '.bta-react-select__control input',      // Structural fallback
    '.timeslots-select input'                // Structural fallback
  ].join(', ');

  const PRICE_SELECTORS = [
    '.price-container span.ml10',            // Sept 2026 build (stable non-hashed classes)
    'span.sc-iHGNWf.sc-carFqZ.hXgXeP',       // Pre-Sept 2026 build
    'span.hXgXeP'                            // Pre-Sept 2026 build (short)
  ];

  function findPriceElement(doc) {
    if (!doc) return null;
    for (const sel of PRICE_SELECTORS) {
      const el = doc.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  // ============================================================================
  // ⚙️ CONFIGURATION - CHANGE SETTINGS HERE
  // ============================================================================
  
  /**
   * LOGGING TOGGLE
   * Set to false to disable all console logging in production
   * Set to true to enable detailed console logging for debugging
   */
  const ENABLE_LOGGING = false;  // ← CHANGE THIS TO true/false

  // ============================================================================
  // DATE LIST VISIBILITY TOGGLE
  // ============================================================================
  const SHOW_DATE_LIST = false; // Set to false to hide the date list and "Date selected" text

  // ============================================================================
  // WAITLIST URL (used by injectWaitlistButton)
  // ============================================================================
  const WAITLIST_URL = 'https://kennelclubboarding.ca/pages/daycare-waitlist-1';

  // ============================================================================
  // DIAGNOSTIC MODE FLAG
  // ============================================================================
  const DIAGNOSTIC_MODE = false; // Set to false to disable diagnostic UI

  // ============================================================================
  // FLEX BOOKING CONFIGURATION
  // ============================================================================
  const FLEX_CONFIG = {
    // Shopify variant IDs for each Flex Booking product ($0.01 each, qty = fee in cents)
    flexVariantIds: [
      49718668984557,  // Flex Booking Protection
      49718669246701,  // Flex Booking Protection 2
      49718669410541,  // Flex Booking Protection 3
      49718669508845,  // Flex Booking Protection 4
      49718669902061,  // Flex Booking Protection 5
    ],

    // $0 product added when customer actively declines protection
    noProtectionVariantId: 49732858446061,

    // Fixed fee products — available for manual use in Shopify admin when needed
    cancellationFeeVariantId: 49719101718765,
    transferFeeVariantId:     49718671474925,

    // Fee tier structure: [maxOrderValue, percentage]
    // Inverse scale — raised 7% from original tiers, no cancellation or transfer fees
    feeTiers: [
      [50,       0.0481],
      [100,      0.0535],
      [200,      0.0449],
      [400,      0.0375],
      [600,      0.0235],
      [1000,     0.0150],
      [Infinity, 0.0107],
    ],

    feeFloor: 1.50,

    calculateFee(orderAmount) {
      const rate = this.getTierRate(orderAmount);
      const exact = Math.max(this.feeFloor, orderAmount * rate);
      return Math.round(exact * 4) / 4;
    },

    getTierRate(amount) {
      for (const [max, pct] of this.feeTiers) {
        if (amount <= max) return pct;
      }
      return 0.0107;
    },

    feeToQuantity(fee) {
      return Math.round(fee * 100);
    }
  };

  // ============================================================================
  // FLEX STATE - tracks customer decision for this booking session
  // ============================================================================
  const flexState = {
    decision: null,  // 'yes' or 'no'
    shown: false,    // whether modal has been shown this session
    sessionId: null, // unique ID for this booking batch

    reset() {
      this.decision = null;
      this.shown = false;
      this.sessionId = null;
      log('flexState reset');
    }
  };

  // ============================================================================
  // LOGGING UTILITY
  // ============================================================================
  function log(...args) {
    if (ENABLE_LOGGING) {
      console.log('[BTA Daycare]', ...args);
    }
  }

  // ============================================================================
  // TIME CONFIGURATION (same as boarding)
  // ============================================================================
  const TIME_CONFIG = {
    // Drop-off times
    weekdayAM: { start: '7:00 AM', end: '8:45 AM' },
    weekdayPM: { start: '4:00 PM', end: '5:45 PM' },
    weekendAM: { start: '8:00 AM', end: '8:45 AM' },
    weekendPM: { start: '5:00 PM', end: '5:45 PM' },
    
    // Pick-up times (same as drop-off for daycare)
    pickupWeekdayAM: { start: '7:00 AM', end: '8:45 AM' },
    pickupWeekdayPM: { start: '4:00 PM', end: '5:45 PM' },
    pickupWeekendAM: { start: '8:00 AM', end: '8:45 AM' },
    pickupWeekendPM: { start: '5:00 PM', end: '5:45 PM' }
  };

  // ============================================================================
  // HOLIDAY CALCULATIONS (for time windows)
  // ============================================================================
  function getGoodFriday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    
    const easter = new Date(year, month - 1, day);
    const goodFriday = new Date(easter);
    goodFriday.setDate(easter.getDate() - 2);
    return goodFriday;
  }

  function getVictoriaDay(year) {
    const may25 = new Date(year, 4, 25);
    const dayOfWeek = may25.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const victoriaDay = new Date(may25);
    victoriaDay.setDate(25 - daysToMonday);
    return victoriaDay;
  }

  function getCivicHoliday(year) {
    const aug1 = new Date(year, 7, 1);
    const dayOfWeek = aug1.getDay();
    const daysToMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 8 - dayOfWeek);
    const civicHoliday = new Date(aug1);
    civicHoliday.setDate(1 + daysToMonday);
    return civicHoliday;
  }

  function getLabourDay(year) {
    const sep1 = new Date(year, 8, 1);
    const dayOfWeek = sep1.getDay();
    const daysToMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 8 - dayOfWeek);
    const labourDay = new Date(sep1);
    labourDay.setDate(1 + daysToMonday);
    return labourDay;
  }

  function getThanksgiving(year) {
    const oct1 = new Date(year, 9, 1);
    const dayOfWeek = oct1.getDay();
    const daysToMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 8 - dayOfWeek);
    const thanksgiving = new Date(oct1);
    thanksgiving.setDate(1 + daysToMonday + 7);
    return thanksgiving;
  }

  function getFamilyDay(year) {
    const feb1 = new Date(year, 1, 1);
    const dayOfWeek = feb1.getDay();
    const daysToMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 8 - dayOfWeek);
    const familyDay = new Date(feb1);
    familyDay.setDate(1 + daysToMonday + 14);
    return familyDay;
  }

  function isHoliday(date) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    const fixedHolidays = [
      { month: 1, day: 1 },
      { month: 7, day: 1 },
      { month: 11, day: 11 },
      { month: 12, day: 25 },
      { month: 12, day: 26 }
    ];
    
    for (const holiday of fixedHolidays) {
      if (month === holiday.month && day === holiday.day) return true;
    }
    
    const calculatedHolidays = [
      getGoodFriday(year),
      getVictoriaDay(year),
      getCivicHoliday(year),
      getLabourDay(year),
      getThanksgiving(year),
      getFamilyDay(year)
    ];
    
    for (const holiday of calculatedHolidays) {
      if (date.toDateString() === holiday.toDateString()) return true;
    }
    
    return false;
  }

  function isWeekend(date) {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  function getTimeWindows(date) {
    const weekend = isWeekend(date) || isHoliday(date);
    
    // For daycare, drop-off is AM and pick-up is PM
    return {
      dropOff: weekend ? TIME_CONFIG.weekendAM : TIME_CONFIG.weekdayAM,
      pickUp: weekend ? TIME_CONFIG.pickupWeekendPM : TIME_CONFIG.pickupWeekdayPM
    };
  }

  function getDayName(date) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  }

  function formatDate(date) {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  // ============================================================================
  // FLEX BOOKING — PRICE READER & MODAL
  // ============================================================================

  function getBookingAmountFromIframe(doc) {
    if (!doc) return null;

    let priceElement = findPriceElement(doc);
    if (!priceElement) {
      const allSpans = doc.querySelectorAll('span');
      for (const span of allSpans) {
        if (span.textContent.includes('$') && span.textContent.match(/\d/)) {
          const prevText = span.previousSibling?.textContent || span.parentElement?.textContent || '';
          if (prevText.toLowerCase().includes('price')) {
            priceElement = span;
            break;
          }
        }
      }
    }

    if (!priceElement) {
      log('⚠ getBookingAmountFromIframe: could not find price element');
      return null;
    }

    const raw = priceElement.textContent.trim().replace(/[^0-9.]/g, '');
    const amount = parseFloat(raw);
    if (isNaN(amount) || amount <= 0) {
      log('⚠ getBookingAmountFromIframe: could not parse price:', priceElement.textContent);
      return null;
    }

    log(`✓ getBookingAmountFromIframe: $${amount}`);
    return amount;
  }

  function showFlexModal(bookingAmount) {
    return new Promise((resolve) => {
      log('Showing Flex Booking modal');

      const flexFee    = bookingAmount ? FLEX_CONFIG.calculateFee(bookingAmount) : null;
      const refundBack = bookingAmount || null;
      const fmt        = (n) => n !== null ? '$' + n.toFixed(2) : '—';
      const feeLabel   = flexFee !== null ? fmt(flexFee) : '';

      const modalId = 'flex-booking-modal-' + Date.now();

      const totalPaid = bookingAmount && flexFee !== null ? bookingAmount + flexFee : null;
      const breakdownHTML = (bookingAmount && flexFee !== null) ? `
        <div style="
          background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;
          padding:11px 14px;margin:10px 0 14px;font-size:13px;
        ">
          <div style="font-weight:600;color:#374151;margin-bottom:7px;">If you cancel:</div>
          <div style="display:flex;justify-content:space-between;color:#6b7280;margin-bottom:3px;">
            <span>Total paid (booking + protection)</span><span>${fmt(totalPaid)}</span>
          </div>
          <div style="display:flex;justify-content:space-between;color:#6b7280;margin-bottom:3px;">
            <span>Protection cost (non-refundable)</span><span>− ${fmt(flexFee)}</span>
          </div>
          <div style="border-top:1px solid #e5e7eb;margin:7px 0;"></div>
          <div style="display:flex;justify-content:space-between;font-weight:700;color:#15803d;">
            <span>You receive back</span><span>${fmt(bookingAmount)}</span>
          </div>
        </div>` : '';

      const modalHTML = `
        <style>
          @keyframes kcbFlexFadeIn  { from{opacity:0} to{opacity:1} }
          @keyframes kcbFlexSlideUp { from{transform:translateY(30px);opacity:0} to{transform:translateY(0);opacity:1} }
          @keyframes kcbFlexFadeOut { from{opacity:1} to{opacity:0} }

          .kcb-flex-option {
            display:flex;align-items:flex-start;gap:13px;
            border:2px solid #e5e7eb;border-radius:10px;
            padding:14px 16px;cursor:pointer;
            transition:border-color 0.18s,background 0.18s;
            background:white;margin-bottom:10px;
          }
          .kcb-flex-option:last-of-type { margin-bottom:0; }
          .kcb-flex-option.selected-yes { border-color:#BD9B60;background:#fdfaf5; }
          .kcb-flex-option.selected-no  { border-color:#d1d5db;background:#f9fafb; }
          .kcb-flex-radio {
            width:20px;height:20px;border-radius:50%;
            border:2px solid #d1d5db;flex-shrink:0;margin-top:2px;
            display:flex;align-items:center;justify-content:center;
            transition:border-color 0.18s,background 0.18s;
          }
          .kcb-flex-option.selected-yes .kcb-flex-radio { border-color:#BD9B60;background:#BD9B60; }
          .kcb-flex-option.selected-yes .kcb-flex-radio::after {
            content:'';display:block;width:8px;height:8px;border-radius:50%;background:white;
          }
          .kcb-flex-option.selected-no .kcb-flex-radio { border-color:#9ca3af;background:#9ca3af; }
          .kcb-flex-option.selected-no .kcb-flex-radio::after {
            content:'';display:block;width:8px;height:8px;border-radius:50%;background:white;
          }
          #kcb-flex-confirm-btn {
            width:100%;padding:14px;border:none;border-radius:8px;
            background:#BD9B60;color:white;font-size:15px;font-weight:700;
            cursor:pointer;transition:background 0.18s;letter-spacing:0.01em;margin-top:18px;
          }
          #kcb-flex-confirm-btn:hover { background:#a37f4a; }
          #kcb-flex-learn-btn {
            background:none;border:none;padding:0;
            color:#BD9B60;font-size:13px;font-weight:600;
            cursor:pointer;text-decoration:underline;
            display:inline-flex;align-items:center;gap:4px;
          }
          @media(max-width:480px){
            #${modalId} .kcb-flex-inner {
              margin:0 auto !important;width:100% !important;
              /* v4.7.1: 94vh cap removed - height is now fitted to the visible screen in JS */
            }
          }
        </style>

        <div id="${modalId}" style="
          position:fixed;inset:0;z-index:9999999;
          background:rgba(0,0,0,0.6);
          display:flex;align-items:flex-start;justify-content:center;
          padding:16px 12px;box-sizing:border-box;
          animation:kcbFlexFadeIn 0.25s ease-out;
          overflow-y:auto;
        ">
          <div class="kcb-flex-inner" style="
            background:white;border-radius:14px;
            width:100%;max-width:480px;
            box-shadow:0 20px 60px rgba(0,0,0,0.25);
            font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
            animation:kcbFlexSlideUp 0.28s ease-out;
            display:flex;flex-direction:column;
            max-height:calc(100vh - 32px);max-height:calc(100dvh - 32px);
            overflow-x:hidden;overflow-y:auto;
          ">

            <div style="height:5px;background:#BD9B60;flex:0 0 auto;"></div>
            <!-- v4.7.1: scrolling middle - everything except the pinned footer -->
            <div class="kcb-flex-scroll" style="flex:1 1 auto;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;">

            <div style="padding:22px 24px 0;">
              <div style="display:flex;align-items:center;gap:12px;margin-bottom:6px;">
                <div style="
                  width:38px;height:38px;border-radius:50%;
                  background:#fdf6e9;border:2px solid #BD9B60;
                  display:flex;align-items:center;justify-content:center;
                  font-size:18px;flex-shrink:0;
                ">🛡️</div>
                <div>
                  <div style="font-size:18px;font-weight:700;color:#1f2937;line-height:1.2;">
                    Booking Protection
                  </div>
                  <div style="font-size:13px;color:#6b7280;margin-top:2px;">
                    Life is unpredictable. Flex Booking keeps your plans protected.
                  </div>
                </div>
              </div>
            </div>

            <div style="padding:16px 24px 0;">

              <div class="kcb-flex-option selected-yes" id="kcb-opt-yes">
                <div class="kcb-flex-radio"></div>
                <div style="flex:1;">
                  <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:6px;">
                    <div style="font-size:15px;font-weight:700;color:#1f2937;">Protect my booking</div>
                    <div style="
                      background:#fdf6e9;border:1px solid #BD9B60;
                      color:#92400e;font-size:13px;font-weight:700;
                      padding:3px 10px;border-radius:20px;white-space:nowrap;
                    ">${feeLabel}</div>
                  </div>
                  <div style="font-size:13px;color:#15803d;font-weight:600;margin-top:4px;">
                    ✓ Refundable &amp; Transferable
                  </div>
                  <div style="font-size:12.5px;color:#6b7280;margin-top:3px;line-height:1.5;">
                    Cancel or transfer up to 6pm the day before your booking — fully refundable &amp; transferable.
                  </div>
                </div>
              </div>

              <div class="kcb-flex-option" id="kcb-opt-no">
                <div class="kcb-flex-radio"></div>
                <div style="flex:1;">
                  <div style="font-size:15px;font-weight:700;color:#374151;">No protection</div>
                  <div style="font-size:13px;color:#dc2626;font-weight:600;margin-top:4px;">
                    ✕ Non-Refundable &amp; Non-Transferable
                  </div>
                  <div style="font-size:12.5px;color:#6b7280;margin-top:3px;line-height:1.5;">
                    I accept this booking is final and cannot be cancelled or transferred.
                  </div>
                </div>
              </div>


              <div style="margin-top:14px;">
                <button id="kcb-flex-learn-btn">▸ Conditions</button>
              </div>

              <div id="kcb-flex-learn-content" style="display:none;margin-top:12px;">
                <div style="
                  background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;
                  padding:16px;font-size:13px;color:#374151;line-height:1.65;
                ">
                  <div style="font-weight:700;color:#1f2937;margin-bottom:5px;">Cancellations</div>
                  <p style="margin:0 0 8px;">
                    Submit a cancellation request by 6pm the day before your booking and receive a full refund of your <strong>booking amount</strong>. The cost of Flex Protection is non-refundable.
                  </p>
                  ${breakdownHTML}
                  <p style="margin:0 0 14px;color:#6b7280;">
                    Cancellations must be requested by 6pm the day before your booking. Requests received
                    after 6pm the day before — or on, or after, the day of your booking — cannot be cancelled or
                    transferred, and that day will be charged in full. Each booked date is treated
                    independently; cancelling one date does not affect your other booked dates.
                  </p>

                  <div style="font-weight:700;color:#1f2937;margin-bottom:5px;">Transfers</div>
                  <p style="margin:0 0 8px;">
                    Transfer requests must be submitted by 6pm the day before the booking you wish to
                    transfer. Requests received after 6pm the day before — or on, or after, the day of your booking —
                    cannot be transferred or cancelled, and that day will be charged in full. Each booked
                    date can be transferred independently, subject to availability, at no additional charge.
                  </p>

                  <div style="font-weight:700;color:#1f2937;margin-bottom:5px;">Please note</div>
                  <p style="margin:0 0 8px;color:#6b7280;">
                    No-show bookings are not eligible for a refund or transfer regardless of Flex Booking Protection status.
                  </p>
                  <p style="margin:0 0 8px;color:#6b7280;">
                    The cost of Flex Booking Protection is non-refundable in all cases.
                    All refund and transfer requests must be submitted by email to
                    <a href="mailto:info@kennelclubboarding.ca" style="color:#BD9B60;font-weight:600;">
                      info@kennelclubboarding.ca
                    </a>
                  </p>
                  <p style="margin:0;color:#6b7280;">
                    <a href="https://kennelclubboarding.ca/policies/refund-policy"
                       target="_blank"
                       style="color:#BD9B60;font-weight:600;">
                      View full Cancellation &amp; Refund Policy →
                    </a>
                  </p>
                </div>
              </div>

            </div>

            </div><!-- /kcb-flex-scroll -->
            <!-- v4.7.1: pinned footer - booking total + Confirm always on screen -->
            <div class="kcb-flex-footer" style="flex:0 0 auto;padding:12px 24px 16px;background:white;border-top:1px solid #f3f4f6;box-shadow:0 -6px 14px rgba(0,0,0,0.05);">
              ${bookingAmount && flexFee !== null ? `
              <div style="
                display:flex;justify-content:space-between;align-items:center;
                padding:0 0 10px;margin-top:0;
                border-top:none;
              ">
                <span style="font-size:13px;color:#374151;">Booking total</span>
                <div id="kcb-flex-total-display" style="font-size:13px;font-weight:700;color:#1f2937;">${fmt(bookingAmount + flexFee)}</div>
              </div>
              ` : ''}
              <button id="kcb-flex-confirm-btn">Confirm</button>
            </div>

          </div>
        </div>
      `;

      const container = document.createElement('div');
      container.innerHTML = modalHTML;
      document.body.appendChild(container);
      document.body.style.overflow = 'hidden';

      // v4.7.1: size the card to the visible screen (visualViewport excludes the
      // browser toolbars) so the pinned Confirm footer is always on screen
      const kcbFlexFit = () => {
        try {
          const fitInner = container.querySelector('.kcb-flex-inner');
          if (!fitInner || !fitInner.isConnected) {
            if (window.visualViewport) window.visualViewport.removeEventListener('resize', kcbFlexFit);
            window.removeEventListener('resize', kcbFlexFit);
            return;
          }
          const visibleH = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
          fitInner.style.maxHeight = Math.max(300, Math.floor(visibleH - 32)) + 'px';
        } catch (fitErr) { /* never block the modal */ }
      };
      kcbFlexFit();
      if (window.visualViewport) window.visualViewport.addEventListener('resize', kcbFlexFit);
      window.addEventListener('resize', kcbFlexFit);

      let currentDecision = 'yes';

      const optYes = document.getElementById('kcb-opt-yes');
      const optNo  = document.getElementById('kcb-opt-no');

      const setSelection = (decision) => {
        currentDecision = decision;
        if (decision === 'yes') {
          optYes.classList.add('selected-yes');
          optYes.classList.remove('selected-no');
          optNo.classList.remove('selected-yes', 'selected-no');
        } else {
          optNo.classList.add('selected-no');
          optNo.classList.remove('selected-yes');
          optYes.classList.remove('selected-yes', 'selected-no');
        }
        const totalDisplay = document.getElementById('kcb-flex-total-display');
        if (totalDisplay && flexFee !== null) {
          totalDisplay.textContent = decision === 'yes' ? fmt(bookingAmount + flexFee) : fmt(bookingAmount);
        }
      };

      optYes.addEventListener('click', () => setSelection('yes'));
      optNo.addEventListener('click',  () => setSelection('no'));

      const learnBtn     = document.getElementById('kcb-flex-learn-btn');
      const learnContent = document.getElementById('kcb-flex-learn-content');
      learnBtn.addEventListener('click', () => {
        const open = learnContent.style.display !== 'none';
        learnContent.style.display = open ? 'none' : 'block';
        learnBtn.innerHTML = open ? '▸ Conditions' : '▾ Hide conditions';
      });

      const closeModal = (decision) => {
        const overlay = document.getElementById(modalId);
        if (overlay) overlay.style.animation = 'kcbFlexFadeOut 0.2s ease-out';
        setTimeout(() => {
          container.remove();
          document.body.style.overflow = 'auto';
          log(`✓ Flex modal confirmed — decision: ${decision}`);
          setTimeout(() => resolve(decision), 50);
        }, 200);
      };

      const showSecondConfirmation = () => {
        const inner = container.querySelector('.kcb-flex-inner');
        if (!inner) { closeModal('no'); return; }

        inner.innerHTML = `
          <div style="height:5px;background:#e5e7eb;border-radius:14px 14px 0 0;"></div>
          <div style="padding:28px 24px 24px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
            <div style="text-align:center;margin-bottom:20px;">
              <div style="font-size:36px;margin-bottom:10px;">⚠️</div>
              <div style="font-size:17px;font-weight:700;color:#1f2937;margin-bottom:8px;">
                Just to confirm
              </div>
              <div style="
                font-size:14px;color:#374151;line-height:1.6;
                background:#fef2f2;border:1px solid #fecaca;
                border-radius:8px;padding:14px 16px;text-align:left;
              ">
                This booking will be <strong>non-refundable and non-transferable</strong>.
                No cancellations, refunds, or date changes will be possible after checkout.
              </div>
            </div>
            <div style="display:flex;flex-direction:column;gap:10px;margin-top:4px;">
              <button id="kcb-flex-goback-btn" style="
                width:100%;padding:14px;border-radius:8px;
                border:none;background:#BD9B60;
                color:white;font-size:15px;font-weight:700;
                cursor:pointer;transition:background 0.18s;
              ">Yes — protect my booking for ${feeLabel}</button>
              <button id="kcb-flex-confirm2-btn" style="
                width:100%;padding:12px;border-radius:8px;
                border:1.5px solid #d1d5db;background:white;
                color:#6b7280;font-size:13px;font-weight:500;
                cursor:pointer;transition:all 0.18s;line-height:1.3;
              ">No thanks — I accept this booking is non-refundable and non-transferable</button>
            </div>
          </div>
        `;

        document.getElementById('kcb-flex-goback-btn').addEventListener('click', (e) => {
          e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
          closeModal('yes');
        });

        document.getElementById('kcb-flex-confirm2-btn').addEventListener('click', (e) => {
          e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
          closeModal('no');
        });
      };

      document.getElementById('kcb-flex-confirm-btn').addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
        if (currentDecision === 'no') {
          showSecondConfirmation();
        } else {
          closeModal('yes');
        }
      });
    });
  }

  // ============================================================================
  // FETCH INTERCEPTOR - Inject daycare time properties into cart/add.js
  // ============================================================================
  const originalFetch = window.fetch;
  window.fetch = async function(url, options) {
    // Only intercept POST requests to /cart/add.js with FormData
    if (url === '/cart/add.js' && options?.body instanceof FormData) {
      log('📦 Intercepting cart/add.js request');
      
      const formData = options.body;
      
      // Check if this is a daycare booking by looking for the Date property
      let dateValue = null;
      let timeValue = null;
      
      for (const [key, value] of formData.entries()) {
        if (key === 'properties[Date]') {
          dateValue = value;
        }
        if (key === 'properties[Time]') {
          timeValue = value;
        }
      }
      
      if (dateValue) {
        log('✓ Found daycare booking with Date:', dateValue);

        // ── FLEX BOOKING MODAL ───────────────────────────────────────────
        // Show once per session before the first cart add proceeds.
        // Subsequent date adds in the same session skip the modal.
        if (!flexState.shown) {
          log('Flex: showing modal before cart add');
          const doc = getIframeDoc();
          const bookingAmount = getBookingAmountFromIframe(doc);
          const decision = await showFlexModal(bookingAmount);
          flexState.decision = decision;
          flexState.shown = true;
          log(`Flex: decision="${decision}"`);
          // Now show the overlay that was suppressed during the modal
          showBookNowLoadingOverlay();
        }

        // Inject Flex line properties on every daycare cart add
        const flexValue = flexState.decision === 'yes' ? 'Yes' : 'No';
        formData.append('properties[_flexProtection]', flexValue);

        // Inject booking session ID — ties all dates in this batch together
        // Used by cart to group dates and their single Flex item correctly
        if (flexState.sessionId) {
          formData.append('properties[_bookingSessionId]', flexState.sessionId);
        }

        // Private property — calculated Flex fee in dollars, read by cart to set variant quantity
        if (flexState.decision === 'yes') {
          const docForFee = getIframeDoc();
          const bookingAmountForFee = getBookingAmountFromIframe(docForFee);
          if (bookingAmountForFee) {
            const calculatedFee = FLEX_CONFIG.calculateFee(bookingAmountForFee);
            formData.append('properties[_flexFee]', calculatedFee.toFixed(2));
            log(`  _flexFee: ${calculatedFee.toFixed(2)}`);
          }
        }

        const flexPublicValue = flexState.decision === 'yes'
          ? '✓ Protected — Refundable & Transferable'
          : '✕ No Protection — Non-Refundable & Non-Transferable';
        formData.append('properties[Booking Protection]', flexPublicValue);

        log(`  _flexProtection: ${flexValue}`);
        log(`  Booking Protection: ${flexPublicValue}`);
        // ── END FLEX BOOKING MODAL ───────────────────────────────────────
        
        // Parse the date (format: MM/DD/YYYY)
        const dateParts = dateValue.split('/');
        if (dateParts.length === 3) {
          const month = parseInt(dateParts[0], 10) - 1; // JS months are 0-indexed
          const day = parseInt(dateParts[1], 10);
          const year = parseInt(dateParts[2], 10);
          const bookingDate = new Date(year, month, day);
          
          // Get time windows based on date
          const timeWindows = getTimeWindows(bookingDate);
          const dayName = getDayName(bookingDate);
          const formattedDate = formatDate(bookingDate);
          
          // Add our custom properties
          formData.append('properties[dropOffTime]', `${timeWindows.dropOff.start} - ${timeWindows.dropOff.end}`);
          formData.append('properties[dropOffDay]', dayName);
          formData.append('properties[dropOffDate]', formattedDate);
          formData.append('properties[pickUpTime]', `${timeWindows.pickUp.start} - ${timeWindows.pickUp.end}`);
          formData.append('properties[pickUpDay]', dayName); // Same day for daycare
          formData.append('properties[pickUpDate]', formattedDate); // Same date for daycare
          
          log('✓ Injected daycare time properties:');
          log(`  dropOffTime: ${timeWindows.dropOff.start} - ${timeWindows.dropOff.end}`);
          log(`  dropOffDay: ${dayName}`);
          log(`  dropOffDate: ${formattedDate}`);
          log(`  pickUpTime: ${timeWindows.pickUp.start} - ${timeWindows.pickUp.end}`);
          log(`  pickUpDay: ${dayName}`);
          log(`  pickUpDate: ${formattedDate}`);
        }
      }
    }
    
    // Call original fetch
    return originalFetch.apply(this, arguments);
  };

  log('✓ Fetch interceptor installed for daycare time properties');
  
  // ============================================================================
  // BEHAVIOR RECORDING SYSTEM WITH AUTO-SAVE
  // ============================================================================
  const behaviorLog = [];
  let recordingStartTime = Date.now();
  let isRecording = true;
  let mutationObserver = null;
  let justNavigatedMonth = false; // Track if we just navigated to a new month
  
  // Auto-save logs before page unload
  // Auto-save logs before page unload
  function setupAutoSave() {
    // DISABLED IN PRODUCTION - Only runs when DIAGNOSTIC_MODE = true
    if (!DIAGNOSTIC_MODE) return;
    
    // Save logs to localStorage continuously
    function saveLogs() {
      try {
        localStorage.setItem('bta_diagnostic_log', JSON.stringify({
          logs: behaviorLog,
          timestamp: new Date().toISOString(),
          url: window.location.href
        }));
      } catch (e) {
        // console.error('[BTA Main] Failed to save logs:', e);
      }
    }
    
    // Save every 2 seconds
    setInterval(saveLogs, 2000);
    
    // Save before page unload/navigation
    window.addEventListener('beforeunload', saveLogs);
    
    // Also monitor for navigation inside iframe
    const checkForPreviousLogs = () => {
      try {
        const savedData = localStorage.getItem('bta_diagnostic_log');
        if (savedData) {
          const data = JSON.parse(savedData);
          // If logs exist and are recent (within last 2 minutes), download them
          const logAge = Date.now() - new Date(data.timestamp).getTime();
          if (logAge < 120000 && data.logs.length > 0) {
            // console.log('[BTA Main] 🎉 Found previous session logs! Auto-downloading...');
            downloadPreviousLogs(data);
          }
        }
      } catch (e) {
        // console.error('[BTA Main] Failed to check previous logs:', e);
      }
    };
    
    // Check for previous logs on load
    setTimeout(checkForPreviousLogs, 1000);
  }
  
  function downloadPreviousLogs(data) {
    // DISABLED IN PRODUCTION - Only runs when DIAGNOSTIC_MODE = true
    if (!DIAGNOSTIC_MODE) return;
    const logText = data.logs.map(entry => 
      `[${entry.time}] [${entry.category}] ${entry.message} ${JSON.stringify(entry.data)}`
    ).join('\n');
    
    const fullLog = `BTA Diagnostic Log - Previous Session
Generated: ${data.timestamp}
URL: ${data.url}
Total Events: ${data.logs.length}

${'='.repeat(80)}

${logText}`;
    
    const blob = new Blob([fullLog], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bta-diagnostic-previous-${new Date(data.timestamp).getTime()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    // Clear the saved logs after download
    localStorage.removeItem('bta_diagnostic_log');
    
    // console.log('[BTA Main] ✅ Previous session logs downloaded and cleared');
  }
  
  function recordBehavior(category, message, data = {}) {
    // DISABLED IN PRODUCTION - function does nothing
    return;
    
    /* ORIGINAL FUNCTION - DISABLED
    if (!isRecording) return;
    
    const timestamp = Date.now() - recordingStartTime;
    const entry = {
      timestamp: timestamp,
      time: `+${(timestamp / 1000).toFixed(3)}s`,
      category: category,
      message: message,
      data: data,
      fullTimestamp: new Date().toISOString()
    };
    
    behaviorLog.push(entry);
    
    // Also log to console with color coding - DISABLED IN PRODUCTION
    // const colors = {
    //   'USER_ACTION': '#f59e0b',
    //   'DOM_CHANGE': '#8b5cf6',
    //   'STATE_CHANGE': '#3b82f6',
    //   'BTA_BEHAVIOR': '#10b981',
    //   'AUTOMATION': '#ef4444',
    //   'ERROR': '#dc2626',
    //   'FORM_SUBMIT': '#ec4899',
    //   'BUTTON_CLICK': '#f97316'
    // };
    
    // console.log(
    //   `%c[${entry.time}] [${category}] ${message}`,
    //   `color: ${colors[category] || '#6b7280'}; font-weight: bold`,
    //   data
    // );
    
    // Update UI log display if it exists
    updateLogDisplay();
    */
  }
  
  // Setup mutation observer to watch for timeslot field creation
  let lastClickedDateButton = null;
  let lastClickedDateInfo = null;
  let bookingCountBeforeClick = 0;
  
  function setupMutationObserver() {
    if (!state.iframeDoc) return;
    
    if (mutationObserver) {
      mutationObserver.disconnect();
    }
    
    mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              // Check if timeslot field was added
              if (node.querySelector && node.querySelector(TIMESLOT_INPUT_SELECTOR)) {
                recordBehavior('DOM_CHANGE', '✅ Timeslot field CREATED', {
                  tag: node.tagName,
                  classes: node.className
                });
                
                // Simply trigger timeslot automation
                // The justNavigatedMonth flag will handle whether to force keystrokes
                handleTimeslotAutomation();
              }
            }
          });
        }
      });
    });
    
    mutationObserver.observe(state.iframeDoc.body, {
      childList: true,
      subtree: true
    });
    
    recordBehavior('STATE_CHANGE', 'Mutation observer started - watching for timeslot field');
  }

  // ============================================================================
  // SHARED STATE
  // ============================================================================
  window.BTAMultiBooking = {
    selectedDates: new Map(),
    iframe: null,
    iframeDoc: null,
    config: {
      highlightColor: '#BD9B60',
      productPath: 'dog-daycare-near-me',
      scanInterval: 1000 // Scan every 1 second to catch removals quickly
    }
  };

  const state = window.BTAMultiBooking;

  // ============================================================================
  // DIAGNOSTIC UI (WILL BE REMOVED AFTER TESTING)
  // ============================================================================
  let diagnosticUI = null;
  
  // Timeout configuration - all delays in milliseconds
  const timeouts = {
    timeslotDelay: 0,           // Delay before handling timeslot after date click
    plusButtonDelay: 0,          // Delay before clicking plus button after timeslot
    dashesLineDelay: 0,           // Delay before hiding dashes line after plus click
    removalScanDelay: 100,       // Delay before scanning after minus button click (100ms to allow BTA to update DOM)
    timeslotFocusDelay: 0,       // Delay before focusing timeslot input
    timeslotSpaceDelay: 0,       // Delay before sending Space key to timeslot
    minusButtonDelay: 0          // Delay before clicking minus button
  };

  let logDisplayElement = null;
  let hasShownWelcomeMessage = false; // Track if we've shown the multi-date notification

  // ============================================================================
  // USER NOTIFICATION SYSTEM (DISABLED - customers now aware of multi-date)
  // ============================================================================
  function showMultiDateNotification() {
    // Notification disabled - customers are now familiar with multi-date booking
    return;
  }

  function createDiagnosticUI() {
    if (!DIAGNOSTIC_MODE) return;

    const diagBox = document.createElement('div');
    diagBox.id = 'bta-diagnostic-main';
    diagBox.innerHTML = `
      <div id="bta-diag-header" style="
        cursor: move;
        background: #3b82f6;
        color: white;
        padding: 12px 15px;
        font-weight: bold;
        font-size: 14px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        user-select: none;
        gap: 10px;
      ">
        <span>📹 BTA Behavior Recorder</span>
        <div style="display: flex; gap: 8px;">
          <button id="bta-toggle-recording" style="
            background: #10b981;
            border: none;
            color: white;
            padding: 6px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 11px;
            font-weight: bold;
          ">⏸️ Pause</button>
          <button id="bta-clear-log" style="
            background: #f59e0b;
            border: none;
            color: white;
            padding: 6px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 11px;
            font-weight: bold;
          ">🗑️ Clear</button>
          <button id="bta-download-log" style="
            background: #10b981;
            border: none;
            color: white;
            padding: 6px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 11px;
            font-weight: bold;
          ">💾 Download</button>
        </div>
      </div>
      
      <div style="
        background: #fef3c7;
        padding: 10px 15px;
        border-bottom: 2px solid #f59e0b;
        font-size: 11px;
        color: #92400e;
        line-height: 1.4;
      ">
        <strong>Test Process:</strong> Select dates, navigate months, remove dates. All behavior is logged below.
        <div style="margin-top: 4px; font-size: 10px;">
          Log entries: <span id="bta-log-count">0</span>
        </div>
      </div>

      <div id="bta-log-display" style="
        background: #1f2937;
        color: #e5e7eb;
        padding: 15px;
        max-height: 400px;
        overflow-y: auto;
        font-size: 11px;
        font-family: 'Courier New', monospace;
        line-height: 1.6;
      ">
        <div style="color: #9ca3af; font-style: italic;">Waiting for user actions...</div>
      </div>
    `;

    diagBox.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      width: 500px;
      z-index: 999999;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      border-radius: 6px;
      overflow: hidden;
      background: white;
    `;

    document.body.appendChild(diagBox);
    diagnosticUI = diagBox;
    logDisplayElement = diagBox.querySelector('#bta-log-display');

    // Make draggable
    const header = diagBox.querySelector('#bta-diag-header');
    let isDragging = false;
    let currentX, currentY, initialX, initialY;
    let xOffset = 0, yOffset = 0;

    header.addEventListener('mousedown', (e) => {
      if (e.target.tagName === 'BUTTON') return;
      initialX = e.clientX - xOffset;
      initialY = e.clientY - yOffset;
      isDragging = true;
    });

    document.addEventListener('mousemove', (e) => {
      if (isDragging) {
        e.preventDefault();
        currentX = e.clientX - initialX;
        currentY = e.clientY - initialY;
        xOffset = currentX;
        yOffset = currentY;
        diagBox.style.transform = `translate(${currentX}px, ${currentY}px)`;
      }
    });

    document.addEventListener('mouseup', () => {
      isDragging = false;
    });

    // Button handlers
    diagBox.querySelector('#bta-toggle-recording').addEventListener('click', toggleRecording);
    diagBox.querySelector('#bta-clear-log').addEventListener('click', clearLog);
    diagBox.querySelector('#bta-download-log').addEventListener('click', downloadLog);
    
    recordBehavior('STATE_CHANGE', 'Diagnostic UI created');
  }

  function updateLogDisplay() {
    if (!logDisplayElement || !isRecording) return;
    
    const lastEntries = behaviorLog.slice(-50); // Show last 50 entries
    
    const html = lastEntries.map(entry => {
      const colors = {
        'USER_ACTION': '#fbbf24',
        'DOM_CHANGE': '#a78bfa',
        'STATE_CHANGE': '#60a5fa',
        'BTA_BEHAVIOR': '#34d399',
        'AUTOMATION': '#f87171',
        'ERROR': '#ef4444'
      };
      
      const color = colors[entry.category] || '#9ca3af';
      const dataStr = Object.keys(entry.data).length > 0 ? 
        `<div style="color: #9ca3af; margin-left: 20px; font-size: 10px;">${JSON.stringify(entry.data)}</div>` : '';
      
      return `
        <div style="margin-bottom: 6px;">
          <span style="color: #6b7280;">[${entry.time}]</span>
          <span style="color: ${color}; font-weight: bold;">[${entry.category}]</span>
          <span style="color: #e5e7eb;">${entry.message}</span>
          ${dataStr}
        </div>
      `;
    }).join('');
    
    logDisplayElement.innerHTML = html || '<div style="color: #9ca3af; font-style: italic;">No entries yet...</div>';
    
    // Update count
    const countElement = document.getElementById('bta-log-count');
    if (countElement) {
      countElement.textContent = behaviorLog.length;
    }
    
    // Auto-scroll to bottom
    logDisplayElement.scrollTop = logDisplayElement.scrollHeight;
  }

  function toggleRecording() {
    isRecording = !isRecording;
    const btn = document.getElementById('bta-toggle-recording');
    if (btn) {
      btn.textContent = isRecording ? '⏸️ Pause' : '▶️ Resume';
      btn.style.background = isRecording ? '#10b981' : '#6b7280';
    }
    recordBehavior('STATE_CHANGE', isRecording ? 'Recording resumed' : 'Recording paused');
  }

  function clearLog() {
    behaviorLog.length = 0;
    recordingStartTime = Date.now();
    updateLogDisplay();
    recordBehavior('STATE_CHANGE', 'Log cleared');
  }

  function downloadLog() {
    const logData = {
      generatedAt: new Date().toISOString(),
      sessionDuration: `${((Date.now() - recordingStartTime) / 1000).toFixed(2)}s`,
      totalEntries: behaviorLog.length,
      timeouts: timeouts,
      entries: behaviorLog
    };
    
    const blob = new Blob([JSON.stringify(logData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bta-behavior-log-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    recordBehavior('STATE_CHANGE', 'Log downloaded', { filename: a.download });
  }

  function logDiagnostic(message, type = 'info') {
    // Map to recordBehavior categories
    const categoryMap = {
      success: 'AUTOMATION',
      error: 'ERROR',
      warning: 'ERROR',
      info: 'STATE_CHANGE'
    };
    recordBehavior(categoryMap[type] || 'STATE_CHANGE', message);
  }

  function markTestCompleted(testId) {
    // Placeholder for test completion tracking
  }

  // ============================================================================
  // ORPHANED HIGHLIGHT FIX (Core function)
  // ============================================================================
  
  function applyOrphanedHighlightFix() {
    const doc = state.iframeDoc;
    if (!doc) {
      // console.warn('[BTA Main] Cannot apply orphaned highlight fix - iframe not found');
      return { success: false, cleared: 0, reapplied: 0 };
    }

    const allDateButtons = doc.querySelectorAll('button.rdp-day');
    let clearedCount = 0;

    // Step 1: Clear ALL rdp-day_selected classes from ALL date buttons
    allDateButtons.forEach(btn => {
      const hadClass = btn.classList.contains('rdp-day_selected');
      
      // Remove BTA's class
      btn.classList.remove('rdp-day_selected');
      
      // Remove our custom styling
      btn.style.backgroundColor = '';
      btn.style.color = '';
      btn.style.fontWeight = '';
      btn.style.border = '';
      btn.removeAttribute('data-bta-highlighted');
      
      if (hadClass) {
        clearedCount++;
      }
    });

    // Step 2: Re-apply highlights for currently selected dates only
    let reappliedCount = 0;
    state.selectedDates.forEach((dateInfo) => {
      const cellInfo = findCalendarCell(doc, dateInfo.dateNumber, dateInfo.month, dateInfo.year);
      if (cellInfo) {
        const button = cellInfo.cell.querySelector('button.rdp-day');
        if (button) {
          // Apply our custom gold highlighting
          button.style.backgroundColor = state.config.highlightColor;
          button.style.color = 'white';
          button.style.fontWeight = 'bold';
          button.setAttribute('data-bta-highlighted', 'true');
          
          // Also ensure BTA's selected class is present
          if (!button.classList.contains('rdp-day_selected')) {
            button.classList.add('rdp-day_selected');
          }
          
          reappliedCount++;
        }
      }
    });

    // console.log(`%c[BTA Main] Orphaned highlight fix applied: cleared ${clearedCount}, re-applied ${reappliedCount}`, 'color: #f59e0b; font-weight: bold');

    return {
      success: true,
      cleared: clearedCount,
      reapplied: reappliedCount,
      bookingCount: state.selectedDates.size
    };
  }

  // Diagnostic wrapper for manual testing
  function testOrphanedHighlightFix() {
    logDiagnostic('🧪 Starting orphaned highlight fix test...', 'info');

    const result = applyOrphanedHighlightFix();

    if (!result.success) {
      logDiagnostic('Cannot test fix - iframe not found', 'error');
      alert('❌ Error: Cannot access booking widget iframe');
      return;
    }

    logDiagnostic(`✓ Cleared ${result.cleared} highlighted dates`, 'success');
    logDiagnostic(`✓ Re-applied ${result.reapplied} highlights for selected dates`, 'success');

    const match = result.bookingCount === result.reapplied;
    logDiagnostic(`📊 Results: ${result.bookingCount} in booking list, ${result.reapplied} highlighted`, match ? 'success' : 'error');

    if (match) {
      logDiagnostic('✅ SUCCESS: Highlights now match selected dates!', 'success');
      alert(`✅ Fix successful!\n\n` +
            `• Cleared: ${result.cleared} highlighted dates\n` +
            `• Booking list: ${result.bookingCount} dates\n` +
            `• Re-highlighted: ${result.reapplied} dates\n\n` +
            `Highlights now match booking list!`);
    } else {
      logDiagnostic('⚠️ WARNING: Mismatch between bookings and highlights', 'error');
      alert(`⚠️ Partial fix\n\n` +
            `• Cleared: ${result.cleared} highlighted dates\n` +
            `• Booking list: ${result.bookingCount} dates\n` +
            `• Re-highlighted: ${result.reapplied} dates\n\n` +
            `Some dates may not be visible in current calendar view.`);
    }
  }


  // ============================================================================
  // HIGHLIGHT MODULE
  // ============================================================================
  
  function findCalendarTable(doc) {
    const tables = doc.querySelectorAll('table');
    for (let table of tables) {
      const cells = table.querySelectorAll('td, th');
      for (let cell of cells) {
        if (/^\d{1,2}$/.test(cell.textContent.trim())) {
          return table;
        }
      }
    }
    return null;
  }

  function getCurrentMonth(doc) {
    const allElements = doc.querySelectorAll('*');
    for (let element of allElements) {
      const text = element.textContent.trim();
      const match = text.match(/^(\w+),?\s+(\d{4})$/);
      if (match) {
        return { month: match[1], year: match[2] };
      }
    }
    return null;
  }

  function findCalendarCell(doc, dateNumber, month, year) {
    const table = findCalendarTable(doc);
    if (!table) return null;

    const currentMonth = getCurrentMonth(doc);
    if (!currentMonth || currentMonth.month !== month || currentMonth.year !== year) {
      return null;
    }

    const rows = table.querySelectorAll('tr');
    const candidates = [];
    let totalRows = rows.length;

    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll('td, th');
      cells.forEach((cell, colIndex) => {
        const text = cell.textContent.trim();
        const cellNumber = parseInt(text, 10).toString();
        
        if (cellNumber === dateNumber) {
          const hasGrayedStyle = cell.style.opacity < 1 || 
                                 cell.classList.contains('disabled') ||
                                 cell.classList.contains('other-month');
          
          candidates.push({
            cell: cell,
            row: rowIndex,
            col: colIndex,
            isGrayed: hasGrayedStyle
          });
        }
      });
    });

    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];

    // Multiple candidates - use row position logic
    const dateNum = parseInt(dateNumber, 10);
    
    // Previous month dates (22-31) appear in first row (row 0 or 1)
    // Next month dates (1-7) appear in last row
    // Current month dates are in middle rows
    
    if (dateNum >= 22) {
      // High number - if it's in first rows, it's PREVIOUS month (skip it)
      // We want the one in later rows (current month)
      const middleOrLater = candidates.filter(c => c.row >= 2);
      if (middleOrLater.length > 0) {
        return middleOrLater[0];
      }
    } else if (dateNum <= 7) {
      // Low number - if it's in last row, it's NEXT month (skip it)
      // We want the one in earlier rows (current month)
      const notLastRow = candidates.filter(c => c.row < totalRows - 1);
      if (notLastRow.length > 0) {
        return notLastRow[0];
      }
    }
    
    // Fallback: prefer non-grayed cells
    const activeCandidate = candidates.find(c => !c.isGrayed);
    if (activeCandidate) return activeCandidate;

    // Final fallback: return first candidate
    return candidates[0];
  }

  function highlightDate(dateInfo) {
    const doc = state.iframeDoc;
    if (!doc) return;

    // Need to find the cell and button
    const cellInfo = findCalendarCell(doc, dateInfo.dateNumber, dateInfo.month, dateInfo.year);
    if (!cellInfo) {
      logDiagnostic(`Could not find cell for ${dateInfo.fullDate}`, 'error');
      return;
    }

    // Find the button inside the cell
    const button = cellInfo.cell.querySelector('button.rdp-day');
    if (!button) {
      logDiagnostic(`Could not find button for ${dateInfo.fullDate}`, 'error');
      return;
    }

    // Store references for future use
    dateInfo.cellElement = cellInfo.cell;
    dateInfo.buttonElement = button;
    dateInfo.position = { row: cellInfo.row, col: cellInfo.col };

    // Apply highlight styling to the button
    button.style.backgroundColor = state.config.highlightColor;
    button.style.color = 'white';
    button.style.fontWeight = 'bold';
    button.setAttribute('data-bta-highlighted', 'true');

    logDiagnostic(`Highlighted ${dateInfo.fullDate} at row ${cellInfo.row}, col ${cellInfo.col}`, 'success');
  }

  function unhighlightDate(dateInfo) {
    const doc = state.iframeDoc;
    if (!doc) return;

    // Try to use cached button element first
    if (dateInfo.buttonElement && doc.contains(dateInfo.buttonElement)) {
      dateInfo.buttonElement.style.backgroundColor = '';
      dateInfo.buttonElement.style.color = '';
      dateInfo.buttonElement.style.fontWeight = '';
      dateInfo.buttonElement.style.border = '';
      dateInfo.buttonElement.removeAttribute('data-bta-highlighted');
      dateInfo.buttonElement.classList.remove('rdp-day_selected');
      logDiagnostic(`Unhighlighted date: ${dateInfo.fullDate}`, 'success');
      return;
    }

    // If button not cached or not in DOM, find it again
    const cellInfo = findCalendarCell(doc, dateInfo.dateNumber, dateInfo.month, dateInfo.year);
    if (cellInfo && cellInfo.cell) {
      const button = cellInfo.cell.querySelector('button.rdp-day');
      if (button) {
        button.style.backgroundColor = '';
        button.style.color = '';
        button.style.fontWeight = '';
        button.style.border = '';
        button.removeAttribute('data-bta-highlighted');
        button.classList.remove('rdp-day_selected');
        logDiagnostic(`Unhighlighted date (re-found): ${dateInfo.fullDate}`, 'success');
      } else {
        logDiagnostic(`Could not find button to unhighlight: ${dateInfo.fullDate}`, 'error');
      }
    } else {
      logDiagnostic(`Could not find cell to unhighlight: ${dateInfo.fullDate}`, 'error');
    }
  }

  // ============================================================================
  // EVENT-DRIVEN AUTOMATION
  // ============================================================================

  function findPlusButton(doc) {
    // Look for the specific container class
    const container = doc.querySelector('.add-another-date-container');
    
    if (container) {
      const buttons = Array.from(container.querySelectorAll('button'));
      // console.log('[BTA Main] findPlusButton: Found container by class, has', buttons.length, 'buttons');
      
      if (buttons.length > 0) {
        // console.log('[BTA Main] findPlusButton: Returning button (plus)');
        return buttons[buttons.length - 1];
      }
    }
    
    // console.log('[BTA Main] findPlusButton: No plus button found');
    return null;
  }

  function findMinusButtons(doc) {
    // The minus button SVG path starts with "M15 10a1 1 0 0 1-1 1h-8" (horizontal line = minus)
    // The plus button SVG path includes vertical component "h-3v3" (cross shape = plus)
    
    // Try to find buttons with SVG icons
    const allButtons = Array.from(doc.querySelectorAll('button'));
    const svgButtons = allButtons.filter(btn => btn.querySelector('svg'));
    
    
    
    // Filter to find minus buttons (horizontal line only, no vertical component)
    const minusButtons = svgButtons.filter(btn => {
      const svg = btn.querySelector('svg');
      const path = svg ? svg.querySelector('path') : null;
      const d = path ? path.getAttribute('d') : '';
      
      // Minus sign path typically has "h-8" (horizontal line) but NOT "v3" (vertical line)
      // Plus sign path has both horizontal and vertical components
      const hasHorizontalLine = d.includes('h-8') || d.includes('h8') || d.includes('1h-8');
      const hasVerticalLine = d.includes('v3') || d.includes('v-3');
      
      // Skip navigation buttons (prev/next month)
      const isNavButton = btn.classList.contains('rdp-nav_button') || 
                          btn.classList.contains('rdp-nav_button_previous') ||
                          btn.classList.contains('rdp-nav_button_next');
      
      const isMinus = hasHorizontalLine && !hasVerticalLine && !isNavButton;
      
      if (isMinus) {
        
      }
      
      return isMinus;
    });
    
    
    
    return minusButtons;
  }

  function findMinusButtonAtIndex(doc, index) {
    const minusButtons = findMinusButtons(doc);
    
    logDiagnostic(`Found ${minusButtons.length} minus buttons total`, 'info');
    // console.log('[BTA Main] findMinusButtonAtIndex: Need button at index', index);
    
    if (index >= 0 && index < minusButtons.length) {
      logDiagnostic(`Returning minus button at index ${index}`, 'success');
      // console.log('[BTA Main] findMinusButtonAtIndex: Returning button', index);
      return minusButtons[index];
    }
    
    logDiagnostic(`Minus button index ${index} out of range (found ${minusButtons.length} minus buttons)`, 'error');
    // console.log('[BTA Main] findMinusButtonAtIndex: Index out of range!');
    return null;
  }

  function clickPlusButton() {
    const doc = state.iframeDoc;
    if (!doc) return;

    const btn = findPlusButton(doc);
    if (btn) {
      logDiagnostic('Auto-clicking plus button', 'success');
      btn.click();
      
      // Hide the dashes line after plus button is clicked (configurable delay)
      setTimeout(() => {
        hideDashesLine(doc);
      }, timeouts.dashesLineDelay);
    }
  }

  function handleTimeslotAutomation() {
    const doc = state.iframeDoc;
    if (!doc) return;

    // Only trigger if we have at least 1 date selected
    if (state.selectedDates.size >= 1) {
      // Retry mechanism - wait for timeslot field to appear
      let attempts = 0;
      const maxAttempts = 10;
      
      const trySelectTimeslot = () => {
        attempts++;
        const reactSelectInput = doc.querySelector(TIMESLOT_INPUT_SELECTOR);
        
        recordBehavior('AUTOMATION', `Timeslot automation attempt ${attempts}/${maxAttempts}`, {
          timeslotFieldExists: !!reactSelectInput
        });
        
        if (!reactSelectInput) {
          if (attempts < maxAttempts) {
            setTimeout(trySelectTimeslot, 100);
            return;
          } else {
            recordBehavior('BTA_BEHAVIOR', '⏳ Timeslot field not found - mutation observer will handle when it appears');
            return;
          }
        }
        
        recordBehavior('BTA_BEHAVIOR', `Timeslot field found on attempt ${attempts}`);
        
        const control = reactSelectInput.closest('.bta-react-select__control') || 
                        reactSelectInput.closest('[class*="control"]');
        
        if (control) {
          const text = control.textContent;
          
          // After month navigation, ALWAYS send keystrokes regardless of field content
          // Otherwise, only send if field says "Select"
          const shouldSendKeystrokes = justNavigatedMonth || text.toLowerCase().includes('select');
          
          if (shouldSendKeystrokes) {
            if (justNavigatedMonth) {
              recordBehavior('AUTOMATION', 'Post-navigation: Forcing timeslot selection (Tab, Space, Enter)', {
                fieldText: text
              });
              justNavigatedMonth = false; // Reset flag after using it
            } else {
              recordBehavior('STATE_CHANGE', 'Auto-selecting timeslot...', {
                fieldText: text
              });
            }
            
            // Send Tab, Space, Enter keystroke sequence
            setTimeout(() => {
              // v22: explicitly focus the input (new BTA build uses a react-select "dummyInput")
              try { reactSelectInput.focus(); } catch (e) { /* ignore */ }
              // First send Tab to focus the field
              const tabDown = new KeyboardEvent('keydown', {
                key: 'Tab',
                code: 'Tab',
                keyCode: 9,
                which: 9,
                bubbles: true,
                cancelable: true
              });
              reactSelectInput.dispatchEvent(tabDown);
              
              // Then Space to open dropdown
              setTimeout(() => {
                const spaceDown = new KeyboardEvent('keydown', {
                  key: ' ',
                  code: 'Space',
                  keyCode: 32,
                  which: 32,
                  bubbles: true,
                  cancelable: true
                });
                reactSelectInput.dispatchEvent(spaceDown);
                
                // Then Enter to select first option
                setTimeout(() => {
                  const enterDown = new KeyboardEvent('keydown', {
                    key: 'Enter',
                    code: 'Enter',
                    keyCode: 13,
                    which: 13,
                    bubbles: true,
                    cancelable: true
                  });
                  reactSelectInput.dispatchEvent(enterDown);
                  
                  recordBehavior('AUTOMATION', 'Sent Tab + Space + Enter keys to timeslot');
                }, timeouts.timeslotSpaceDelay);
              }, timeouts.timeslotSpaceDelay);
            }, timeouts.timeslotFocusDelay);
          } else {
            recordBehavior('BTA_BEHAVIOR', 'Timeslot already has value, skipping', {
              fieldText: text
            });
          }
        }
      };
      
      trySelectTimeslot();
    }
  }

  function findMinusButtonForDate(doc, dateText) {
    const allElements = Array.from(doc.querySelectorAll('*'));
    const containers = allElements.filter(el => {
      const text = el.textContent.toLowerCase();
      return (text.includes('additional') || text.includes('max') || text.includes('booking')) && text.length < 200;
    });
    
    for (let container of containers) {
      const containerText = container.textContent;
      
      if (containerText.includes(dateText)) {
        const buttons = Array.from(container.querySelectorAll('button'));
        
        for (let btn of buttons) {
          const btnText = btn.textContent.trim();
          const btnHtml = btn.innerHTML;
          
          if (btnText === '−' || btnText === '-' || btnText === '×' || btnText === 'X' ||
              btnHtml.includes('minus') || btnHtml.includes('remove') || btnHtml.includes('times')) {
            return btn;
          }
        }
        
        if (buttons.length > 0) {
          return buttons[0];
        }
      }
    }
    
    return null;
  }

  function handleDateClick(event) {
    const button = event.target.closest('button');
    if (!button) return;

    // Check if it's a date button
    if (!button.classList.contains('rdp-day')) return;

    const dateText = button.textContent.trim();
    const dateNumber = parseInt(dateText, 10);
    
    // Get clicked cell's position FIRST
    const td = button.closest('td');
    if (!td) return;

    const tr = td.parentElement;
    const tbody = tr.parentElement;
    const clickedRow = Array.from(tbody.children).indexOf(tr);
    const clickedCol = Array.from(tr.children).indexOf(td);
    
    // Determine which month this date belongs to
    const currentMonth = getCurrentMonth(state.iframeDoc);
    if (!currentMonth) return;

    const totalRows = tbody.children.length;
    let clickedDateMonth = currentMonth.month;
    let clickedDateYear = currentMonth.year;

    // Logic: High dates (22-31) in first rows = previous month
    //        Low dates (1-7) in last row = next month
    if (dateNumber >= 22 && clickedRow <= 1) {
      const prevMonthDate = new Date(currentMonth.year, getMonthIndex(currentMonth.month), 0);
      clickedDateMonth = getMonthName(prevMonthDate.getMonth());
      clickedDateYear = prevMonthDate.getFullYear().toString();
    } else if (dateNumber <= 7 && clickedRow >= totalRows - 1) {
      const nextMonthDate = new Date(currentMonth.year, getMonthIndex(currentMonth.month) + 1, 1);
      clickedDateMonth = getMonthName(nextMonthDate.getMonth());
      clickedDateYear = nextMonthDate.getFullYear().toString();
    }

    // PREVENT clicking dates from other months
    if (clickedDateMonth !== currentMonth.month || clickedDateYear !== currentMonth.year) {
      // console.log('%c[BTA Main] ❌ Blocked click on date from another month', 'color: #ef4444; font-weight: bold', {
      //   clickedDate: `${clickedDateMonth} ${dateNumber}, ${clickedDateYear}`,
      //   currentMonth: `${currentMonth.month} ${currentMonth.year}`,
      //   message: 'Please navigate to the correct month first'
      // });
      
      recordBehavior('USER_ACTION', `BLOCKED: Click on date from another month`, {
        clickedDate: `${clickedDateMonth} ${dateNumber}, ${clickedDateYear}`,
        currentMonth: `${currentMonth.month} ${currentMonth.year}`
      });
      
      // Prevent the event from reaching BTA
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      return false;
    }

    // Construct the full date string
    const fullDateToMatch = `${clickedDateMonth} ${dateNumber}, ${clickedDateYear}`;
    
    // DEBUG: Log what we're looking for
    
    
    
    // CHECK IF THIS DATE IS IN OUR STATE (already selected)
    const selectedDatesArray = Array.from(state.selectedDates.values());
    
    // DEBUG: Log all dates in state
    if (ENABLE_LOGGING) {
      selectedDatesArray.forEach((d, i) => {
        console.log(`  [${i}] fullDate: "${d.fullDate}", dateNumber: "${d.dateNumber}", month: "${d.month}", year: "${d.year}"`);
      });
    }
    
    let matchIndex = -1;

    for (let i = 0; i < selectedDatesArray.length; i++) {
      const dateInfo = selectedDatesArray[i];
      if (dateInfo.fullDate === fullDateToMatch) {
        matchIndex = i;
        break;
      }
    }

    const isSelected = matchIndex !== -1;
    
    // DEBUG: Log match result
    
    
    // Store the clicked button, date info, and current booking count for potential retry
    if (!isSelected) {
      lastClickedDateButton = button;
      lastClickedDateInfo = fullDateToMatch;
      bookingCountBeforeClick = findBookingEntries(state.iframeDoc).length;
    }
    
    // Record user action
    recordBehavior('USER_ACTION', `Date clicked: ${fullDateToMatch}`, {
      dateNumber: dateText,
      row: clickedRow,
      col: clickedCol,
      isSelected: isSelected,
      matchIndex: matchIndex,
      stateSize: state.selectedDates.size,
      eventType: event.type
    });
    
    // DIAGNOSTIC: Log BTA's widget state
    const plusBtn = findPlusButton(state.iframeDoc);
    const timeslotField = state.iframeDoc ? state.iframeDoc.querySelector(TIMESLOT_INPUT_SELECTOR) : null;
    const bookingEntries = findBookingEntries(state.iframeDoc);
    
    recordBehavior('BTA_BEHAVIOR', 'Widget state check', {
      plusButtonFound: !!plusBtn,
      timeslotFieldFound: !!timeslotField,
      bookingEntriesCount: bookingEntries.length,
      hasSelectedClass: button.classList.contains('rdp-day_selected'),
      buttonDisabled: button.disabled || button.hasAttribute('disabled')
    });

    // CRITICAL: If selected (found in state), prevent BTA from processing
    if (isSelected) {
      recordBehavior('USER_ACTION', `BLOCKING event - date already selected`, { date: fullDateToMatch });
      
      // Stop the event IMMEDIATELY before BTA sees it
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Only process removal on click, not mousedown (to avoid double processing)
      if (event.type === 'click') {
        const dateInfoToRemove = selectedDatesArray[matchIndex];
        logDiagnostic(`Removing selected date: ${fullDateToMatch} at index ${matchIndex}`, 'info');
        

        // Remove highlight from the clicked button directly
        button.style.backgroundColor = '';
        button.style.color = '';
        button.style.fontWeight = '';
        button.style.border = '';
        button.removeAttribute('data-bta-highlighted');
        button.classList.remove('rdp-day_selected');
        

        // Click the minus button at this index
        setTimeout(() => {
          const minusBtn = findMinusButtonAtIndex(state.iframeDoc, matchIndex);
          
          if (minusBtn) {
            logDiagnostic(`✓ Clicking minus button at index ${matchIndex}`, 'success');
            
            
            // Temporarily make button clickable (in case it's hidden)
            const originalStyle = minusBtn.getAttribute('style') || '';
            minusBtn.style.cssText = 'opacity: 1 !important; pointer-events: auto !important; position: relative !important; width: auto !important; height: auto !important;';
            
            minusBtn.click();
            
            // Restore original style after click
            setTimeout(() => {
              minusBtn.setAttribute('style', originalStyle);
            }, 50);
            
            // Hide the dashes line after removal
            setTimeout(() => {
              hideDashesLine(state.iframeDoc);
            }, timeouts.dashesLineDelay);
            
            // Trigger scan after a short delay to let BTA process the removal
            setTimeout(() => {
              scanAndUpdate();
              logDiagnostic(`Triggered scan after removal`, 'info');
              
            }, timeouts.removalScanDelay);
            
            // Mark tests as completed
            markTestCompleted(3); // Single removal (test #3)
            markTestCompleted(4); // Multiple removal
          } else {
            logDiagnostic(`❌ Could not find minus button at index ${matchIndex}`, 'error');
            
          }
        }, timeouts.minusButtonDelay);
      }

      // IMPORTANT: Return false to ensure event doesn't propagate
      return false;

    } else {
      // ADDING A DATE - let BTA handle it normally
      // console.log('%c[BTA Main] ✓ Date NOT in state - allowing BTA to handle', 'color: #10b981; font-weight: bold');
      logDiagnostic(`✓ Unselected date clicked: ${fullDateToMatch} (BTA will add it)`, 'info');
      
      // DO NOT prevent default - let BTA handle the click
      // We'll detect the addition via scanAndUpdate after automation completes
      
      // Only trigger automation on click, not mousedown
      if (event.type === 'click') {
        // Sequence: Wait for BTA to add → Handle timeslot → Click plus button → Scan
        setTimeout(() => {
          handleTimeslotAutomation();
          
          // Then click plus button after timeslot is handled
          setTimeout(() => {
            clickPlusButton();
            
            // Then scan to sync state (safety net)
            setTimeout(() => {
              scanAndUpdate();
              logDiagnostic('Triggered scan after addition', 'info');
            }, timeouts.removalScanDelay);
            
            // Mark tests as completed
            markTestCompleted(1); // Single add
            markTestCompleted(2); // Multiple add
          }, timeouts.plusButtonDelay);
        }, timeouts.timeslotDelay);
      }
    }
  }

  // Helper function to get month index from name
  function getMonthIndex(monthName) {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months.indexOf(monthName);
  }

  // Helper function to get month name from index
  function getMonthName(monthIndex) {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[monthIndex];
  }

  function handleMonthNavigation(event) {
    const button = event.target.closest('button');
    if (!button) return;

    if (button.classList.contains('rdp-nav_button_next')) {
      recordBehavior('USER_ACTION', 'Next month button clicked');
      justNavigatedMonth = true; // Set flag for first date click after navigation
      markTestCompleted(5);
      
      // Give BTA time to fully render the new month
      setTimeout(() => {
        // Click plus button to reset BTA's state
        clickPlusButton();
        recordBehavior('AUTOMATION', 'Plus button clicked after month navigation');
        
        // Then refresh highlights and sync
        setTimeout(() => {
          updateAllHighlights();
          scanAndUpdate();
          recordBehavior('STATE_CHANGE', 'Month navigation complete (next)');
        }, 100);
      }, 500);
      
    } else if (button.classList.contains('rdp-nav_button_previous')) {
      recordBehavior('USER_ACTION', 'Previous month button clicked');
      justNavigatedMonth = true; // Set flag for first date click after navigation
      markTestCompleted(6);
      
      // Give BTA time to fully render the new month
      setTimeout(() => {
        // Click plus button to reset BTA's state
        clickPlusButton();
        recordBehavior('AUTOMATION', 'Plus button clicked after month navigation');
        
        // Then refresh highlights and sync
        setTimeout(() => {
          updateAllHighlights();
          scanAndUpdate();
          recordBehavior('STATE_CHANGE', 'Month navigation complete (previous)');
        }, 100);
      }, 500);
    }
  }

  function updateAllHighlights() {
    const doc = state.iframeDoc;
    if (!doc) return;

    // First, clear ALL existing highlights (including orphaned ones)
    const highlightedCells = doc.querySelectorAll('[data-bta-highlighted="true"]');
    highlightedCells.forEach(cell => {
      cell.style.backgroundColor = '';
      cell.style.color = '';
      cell.style.fontWeight = '';
      cell.removeAttribute('data-bta-highlighted');
    });

    // Also clear any cells with our highlight color that might not have the attribute
    const allDateButtons = doc.querySelectorAll('button.rdp-day');
    allDateButtons.forEach(btn => {
      const bgColor = btn.style.backgroundColor;
      if (bgColor && (bgColor.includes('189, 155, 96') || bgColor.includes('#BD9B60'))) {
        btn.style.backgroundColor = '';
        btn.style.color = '';
        btn.style.fontWeight = '';
        btn.removeAttribute('data-bta-highlighted');
      }
    });

    // Now re-apply highlights for currently selected dates
    state.selectedDates.forEach((dateInfo) => {
      highlightDate(dateInfo);
    });
  }

  // ============================================================================
  // DOM SCANNING (Fallback for sync)
  // ============================================================================

  function extractDateInfo(bookingElement) {
    const text = bookingElement.textContent;
    const dateMatch = text.match(/(\w+)\s+(\d{1,2}),?\s+(\d{4})/);
    const timeMatch = text.match(/(\d{1,2}:\d{2}\s+(?:AM|PM))/);
    
    if (!dateMatch) return null;

    const month = dateMatch[1];
    const dateNumber = dateMatch[2];
    const year = dateMatch[3];
    const time = timeMatch ? timeMatch[1] : '';
    const id = `${year}-${month}-${dateNumber}-${Date.now()}`;
    
    return {
      id: id,
      fullDate: `${month} ${dateNumber}, ${year}`,
      dateNumber: parseInt(dateNumber, 10).toString(),
      month: month,
      year: year,
      time: time,
      bookingElement: bookingElement,
      cellElement: null,
      position: null,
      timestamp: Date.now()
    };
  }

  function findBookingEntries(doc) {
    const entries = [];
    const seen = new Set();
    const candidates = doc.querySelectorAll('li, div, span, p');
    
    candidates.forEach(element => {
      const text = element.textContent.trim();
      const dateMatches = text.match(/\w+\s+\d{1,2},?\s+\d{4}/g);
      
      if (!dateMatches || dateMatches.length > 1) return;
      if (text.length > 100) return;
      
      const hasTime = /\d{1,2}:\d{2}\s+(?:AM|PM)/.test(text);
      if (!hasTime) return;
      
      const dateInfo = extractDateInfo(element);
      if (!dateInfo) return;
      
      const key = `${dateInfo.year}-${dateInfo.month}-${dateInfo.dateNumber}-${dateInfo.time}`;
      if (seen.has(key)) return;
      
      const isChildOfFound = entries.some(entry => 
        entry.bookingElement !== element && entry.bookingElement.contains(element)
      );
      if (isChildOfFound) return;
      
      entries.push(dateInfo);
      seen.add(key);
    });
    
    return entries;
  }

  // Store the original base price
  let originalBasePrice = null;

  function updatePriceDisplay() {
    const doc = state.iframeDoc;
    if (!doc) return;

    // Try multiple selectors to find the price element
    let priceElement = findPriceElement(doc);
    
    if (!priceElement) {
      // Try finding by text pattern containing "Price:"
      const allSpans = doc.querySelectorAll('span');
      for (const span of allSpans) {
        if (span.textContent.includes('$') && span.textContent.match(/\d/)) {
          const prevText = span.previousSibling?.textContent || span.parentElement?.textContent || '';
          if (prevText.toLowerCase().includes('price')) {
            priceElement = span;
            break;
          }
        }
      }
    }
    
    if (!priceElement) {
      // console.log('[BTA Main] Price element not found - tried multiple selectors');
      return;
    }

    // console.log('[BTA Main] Found price element:', priceElement.className, priceElement.textContent);

    // Get the current price text and extract the price
    const priceText = priceElement.textContent.trim();
    const priceMatch = priceText.match(/\$?([\d,]+\.?\d*)/);
    
    if (!priceMatch) {
      // console.log('[BTA Main] Could not parse price from:', priceText);
      return;
    }

    // Remove commas and convert to number
    const currentDisplayedPrice = parseFloat(priceMatch[1].replace(/,/g, ''));

    // Store the original base price when we first see it (when there's 1 date)
    const dateCount = state.selectedDates.size;
    
    if (dateCount === 1 && originalBasePrice === null) {
      originalBasePrice = currentDisplayedPrice;
      // console.log('[BTA Main] Stored original base price:', originalBasePrice);
      return; // Don't update on first date
    }

    if (dateCount === 0) {
      // Reset when no dates
      originalBasePrice = null;
      // console.log('[BTA Main] Reset base price');
      return;
    }

    if (originalBasePrice === null) {
      // console.log('[BTA Main] Base price not yet stored');
      return;
    }

    // Calculate total price using the ORIGINAL base price, then apply discount
    const rawTotal = originalBasePrice * dateCount;
    
    let discountedTotal = rawTotal;
    if (dateCount >= 10) {
      discountedTotal = rawTotal * 0.80; // 20% off
    } else if (dateCount >= 5) {
      discountedTotal = rawTotal * 0.90; // 10% off
    }
    
    // Update the price display
    priceElement.textContent = `$${discountedTotal.toFixed(2)}`;
    
    // console.log(`[BTA Main] Price updated: ${dateCount} dates × $${originalBasePrice.toFixed(2)} (original base) = $${totalPrice.toFixed(2)}`);
  }

  // ============================================================================
  // MULTI-VISIT DAYCARE DISCOUNT COUNTER WITH INFO MODAL
  // ============================================================================
  
  function showDiscountInfoModal(doc) {
    // Check if modal already exists
    let modal = doc.getElementById('daycare-discount-modal');
    if (modal) {
      modal.style.display = 'flex';
      return;
    }
    
    // Create modal
    modal = doc.createElement('div');
    modal.id = 'daycare-discount-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 999999;
    `;
    
    modal.innerHTML = `
      <div style="
        background: #222222;
        color: #EAEAEA;
        padding: 24px 28px;
        border-radius: 12px;
        max-width: 340px;
        width: 90%;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
        border: 1px solid #BD9B60;
      ">
        <h3 style="
          margin: 0 0 16px 0;
          font-size: 18px;
          font-weight: 600;
          color: #BD9B60;
        ">Multi-Visit Daycare Discounts</h3>
        <ul style="
          margin: 0 0 16px 0;
          padding-left: 20px;
          line-height: 1.8;
          font-size: 14px;
        ">
          <li>Book 5-9 daycare visits in one booking: <strong style="color: #BD9B60;">10% off</strong></li>
          <li>Book 10+ daycare visits in one booking: <strong style="color: #BD9B60;">20% off</strong></li>
        </ul>
        <p style="
          margin: 0 0 20px 0;
          font-size: 13px;
          opacity: 0.8;
        ">Discounts apply automatically at checkout.</p>
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #3a3a3a;">
        <div style="font-size: 12px; color: #999; line-height: 1.5;">
        <strong style="color: #CF4B20;">Please Note:</strong> Daycare bookings are non-refundable and can't be moved to other dates unless Flex Booking Protection is added at checkout.
        </div>
        </div>
        </br>
        <button id="daycare-discount-modal-close" style="
          background: #BD9B60;
          color: #222222;
          border: none;
          padding: 10px 24px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          width: 100%;
        ">Got it!</button>
      </div>
    `;
    
    doc.body.appendChild(modal);
    
    // Close on button click
    const closeBtn = doc.getElementById('daycare-discount-modal-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        modal.style.display = 'none';
      });
    }
    
    // Close on backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.style.display = 'none';
      }
    });
  }
  
  function updateDiscountCounter() {
    const doc = state.iframeDoc;
    if (!doc) return;
    
    const dateCount = state.selectedDates.size;
    
    // Find the calendar table inside the iframe
    const calendarTable = doc.querySelector('table');
    if (!calendarTable) return;
    
    // Find or create the discount counter container inside the iframe
    let counterContainer = doc.getElementById('daycare-discount-counter');
    
    // Create counter if it doesn't exist
    if (!counterContainer) {
      counterContainer = doc.createElement('div');
      counterContainer.id = 'daycare-discount-counter';
      counterContainer.style.cssText = `
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        margin: 0 0 12px 0;
        padding: 10px 14px;
        border-radius: 6px;
        text-align: center;
        font-size: 14px;
        line-height: 1.4;
        transition: all 0.3s ease;
      `;
      
      // Find the month/year header (e.g., "December 2025") to insert after it
      // The calendar structure typically has the month header before the table
      const calendarParent = calendarTable.parentElement;
      if (calendarParent) {
        // Insert right before the calendar table
        calendarParent.insertBefore(counterContainer, calendarTable);
      }
    }
    
    // Info icon HTML (orange colored, clickable)
    const infoIcon = `<span class="discount-info-icon" style="
      color: #EAEAEA;
      cursor: pointer;
      margin-left: 6px;
      font-size: 14px;
    ">ⓘ</span>`;
    
    // Determine the message and styling based on date count
    let message = '';
    let bgColor = '';
    let borderColor = '';
    let textColor = '';
    
    if (dateCount === 0) {
      // No visits - show teaser
      message = `💰 Book 5+ daycare visits together for 10% off • 10+ for 20%! ${infoIcon}`;
      bgColor = '#222222';
      borderColor = '#BD9B60';
      textColor = '#EAEAEA';
    } else if (dateCount < 5) {
      // Progress toward 5-visit discount
      const visitsNeeded = 5 - dateCount;
      message = `📅 ${dateCount} visit${dateCount > 1 ? 's' : ''} in this booking — Add ${visitsNeeded} more for <strong>10% off!</strong> ${infoIcon}`;
      bgColor = '#222222';
      borderColor = '#BD9B60';
      textColor = '#EAEAEA';
    } else if (dateCount >= 5 && dateCount < 10) {
      // 10% discount unlocked, progress toward 20%
      const visitsNeeded = 10 - dateCount;
      message = `<strong>✓ 10% Daycare Discount Applied!</strong><br><span style="font-size: 12px; opacity: 0.85;">Add ${visitsNeeded} more visit${visitsNeeded > 1 ? 's' : ''} to this booking for 20% off</span> ${infoIcon}`;
      bgColor = '#222222';
      borderColor = '#BD9B60';
      textColor = '#EAEAEA';
    } else {
      // 20% discount unlocked - maximum savings
      message = `<strong>🎉 20% Daycare Discount Applied!</strong><br><span style="font-size: 12px; opacity: 0.85;">Maximum savings unlocked!</span> ${infoIcon}`;
      bgColor = 'rgba(189, 155, 96, 0.25)';
      borderColor = '#BD9B60';
      textColor = '#BD9B60';
    }
    
    // Apply styles and content
    counterContainer.style.background = bgColor;
    counterContainer.style.border = `1px solid ${borderColor}`;
    counterContainer.style.color = textColor;
    counterContainer.innerHTML = message;
    
    // Add click handler to info icon
    const infoIconElement = counterContainer.querySelector('.discount-info-icon');
    if (infoIconElement) {
      infoIconElement.addEventListener('click', (e) => {
        e.stopPropagation();
        showDiscountInfoModal(doc);
      });
    }
  }

  function scanAndUpdate() {
    const doc = state.iframeDoc;
    if (!doc) return;

    // Inject real-time availability badge (idempotent - skips if already present)
    injectAvailabilityBadge(doc);

    // Inject waitlist button at the bottom of the widget (idempotent)
    injectWaitlistButton(doc);

    // Replace the month label with a jump-to-month dropdown (idempotent)
    injectMonthDropdown(doc);

    // Track the size before we make any changes
    const sizeBefore = state.selectedDates.size;

    const foundEntries = findBookingEntries(doc);
    const currentDateIds = new Set(foundEntries.map(e => 
      `${e.year}-${e.month}-${e.dateNumber}`
    ));

    // Inject "Dates selected:" label if we have bookings
    if (foundEntries.length > 0) {
      injectDatesLabel(doc);
    }

    // Remove dates no longer in bookings
    const datesToRemove = [];
    state.selectedDates.forEach((dateInfo, id) => {
      const simpleKey = `${dateInfo.year}-${dateInfo.month}-${dateInfo.dateNumber}`;
      if (!currentDateIds.has(simpleKey)) {
        datesToRemove.push(id);
      }
    });

    datesToRemove.forEach(id => {
      const dateInfo = state.selectedDates.get(id);
      unhighlightDate(dateInfo);
      state.selectedDates.delete(id);
    });

    // Add new dates
    foundEntries.forEach(dateInfo => {
      const simpleKey = `${dateInfo.year}-${dateInfo.month}-${dateInfo.dateNumber}`;
      let existing = null;
      
      state.selectedDates.forEach((info, id) => {
        const existingKey = `${info.year}-${info.month}-${info.dateNumber}`;
        if (existingKey === simpleKey) {
          existing = { id, info };
        }
      });

      if (!existing) {
        state.selectedDates.set(dateInfo.id, dateInfo);
        highlightDate(dateInfo);
      }
    });

    // AUTOMATIC FIX: Apply orphaned highlight fix whenever a date is added
    const sizeAfter = state.selectedDates.size;
    if (sizeAfter > sizeBefore) {
      // console.log('%c[BTA Main] 🔧 Date added - applying orphaned highlight fix', 'color: #f59e0b; font-weight: bold');
      applyOrphanedHighlightFix();
      
      // Show welcome notification when first date is added
      if (sizeAfter === 1 && sizeBefore === 0) {
        showMultiDateNotification();
      }
      
      // Log to diagnostic if available
      if (DIAGNOSTIC_MODE) {
        logDiagnostic('🔧 Auto-fix applied: Date added', 'success');
      }
    }

    // Update price display based on number of selected dates
    updatePriceDisplay();
    
    // Update discount counter display
    updateDiscountCounter();
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  function getIframeDoc() {
    // Always get a fresh reference — never cache iframeDoc
    // BTA can replace the iframe document during init; a cached reference becomes stale
    state.iframe = document.querySelector('iframe[title="widget_xcomponent"]');
    if (!state.iframe) return null;
    
    try {
      const doc = state.iframe.contentDocument || state.iframe.contentWindow.document;
      if (!doc || !doc.body) return null;
      state.iframeDoc = doc; // Keep state in sync for other code that reads state.iframeDoc
      return doc;
    } catch (e) {
      return null;
    }
  }

  function injectHideCSS(doc) {
    // Remove existing style tag first to ensure we're always working with fresh doc
    const existing = doc.getElementById('bta-hide-elements-style');
    if (existing) existing.remove();

    const style = doc.createElement('style');
    style.id = 'bta-hide-elements-style';
    style.textContent = `
      /* Hide the "Book additional dates" plus button */
      .add-another-date-container {
        display: none !important;
      }

      /* Hide the timeslot select field - multiple selectors for coverage */
      select[name="time-slot"],
      select[name="timeslot"],
      select[id*="timeslots-select"],
      select[id*="timeslot"],
      label[for*="time-slot"],
      label[for*="timeslot"],
      label[for*="timeslots-select"] {
        display: none !important;
      }

      /* Hide the parent div containing the timeslot field */
      div[class*="bta-react-select"],
      .bta-react-select,
      div[class*="timeslot"] {
        display: none !important;
      }

      /* Hide minus buttons (but not the booking lines they're on) */
      /* Note: We keep them in DOM but hidden so we can still click them programmatically */
      button.sc-uVWWZ.jxtpjn,
      .booking-times button {
        opacity: 0 !important;
        pointer-events: none !important;
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
      }

      ${!SHOW_DATE_LIST ? `
      /* Hide all booking entries when SHOW_DATE_LIST is false */
      .sc-hLBbgP.fScEjD,
      div[class*="booking-time"],
      .booking-times,
      div.sc-hLBbgP {
        display: none !important;
      }
      ` : ''}

      /* Make dates from other months look slightly faded */
      button.rdp-day_outside,
      button.rdp-day[disabled],
      td.rdp-day_outside button.rdp-day {
        opacity: 0.5 !important;
      }

      /* Show not-allowed cursor for past dates and today */
      button.rdp-day.bta-past-date {
        cursor: not-allowed !important;
        opacity: 0.4 !important;
      }

      /* Tooltip styling for month navigation message */
      .bta-month-tooltip {
        position: fixed;
        background: #222222;
        border: 1px solid #BD9B60;
        color: #FFFFFF;
        padding: 12px 16px;
        border-radius: 8px;
        font-size: 14px;
        line-height: 1.4;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 999999;
        pointer-events: none;
        max-width: 280px;
        text-align: center;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        animation: bta-tooltip-fade-in 0.2s ease-out;
      }

      @keyframes bta-tooltip-fade-in {
        from {
          opacity: 0;
          transform: translateY(-5px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @media (max-width: 768px) {
        .bta-month-tooltip {
          max-width: 240px;
          font-size: 13px;
          padding: 10px 14px;
        }
      }

      /* Change checkmark color to #BD9B60 - target all checkmark-related elements */
      svg circle,
      svg path[fill="#00BA88"],
      svg path[stroke="#00BA88"],
      svg[fill="#00BA88"],
      svg[stroke="#00BA88"] {
        fill: #BD9B60 !important;
        stroke: #BD9B60 !important;
      }
      
      /* Also target by size (checkmarks are typically small SVGs) */
      svg[width="16"] path,
      svg[width="20"] path,
      svg[height="16"] path,
      svg[height="20"] path {
        fill: #BD9B60 !important;
        stroke: #BD9B60 !important;
      }

      /* Style for "Dates selected:" label */
      #bta-dates-label {
        font-weight: bold;
        margin-bottom: 10px;
        margin-top: 15px;
        font-size: 16px;
      }
    `;

    doc.head.appendChild(style);
    // console.log('%c[BTA Main] ✓ CSS injected to hide timeslot and plus button', 'color: #10b981; font-weight: bold');
    
    if (DIAGNOSTIC_MODE) {
      logDiagnostic('Hidden: Timeslot field and plus button', 'success');
    }
  }

  function hideDashesLine(doc) {
    // Find all booking-time-label elements
    const labels = doc.querySelectorAll('.booking-time-label');
    
    // Find the one with underscores/dashes
    labels.forEach(label => {
      const text = label.textContent.trim();
      // Check if it contains only underscores, spaces, and placeholder characters
      if (/^[_\s,:]+$/.test(text) || text.includes('________')) {
        // Hide the parent li or div
        const parent = label.closest('li, div');
        if (parent) {
          parent.style.display = 'none';
        }
      }
    });
  }

  function injectAvailabilityBadge(doc) {
    if (!doc) return;

    // Skip if already injected
    if (doc.getElementById('bta-availability-badge')) return;

    // Find the price element using the same multi-selector fallback as updatePriceDisplay
    let priceElement = findPriceElement(doc);
    if (!priceElement) {
      const allSpans = doc.querySelectorAll('span');
      for (const span of allSpans) {
        if (span.textContent.includes('$') && span.textContent.match(/\d/)) {
          const prevText = span.previousSibling?.textContent || span.parentElement?.textContent || '';
          if (prevText.toLowerCase().includes('price')) {
            priceElement = span;
            break;
          }
        }
      }
    }
    if (!priceElement) return;

    // Walk up to find the price row/wrapper (the container that holds the price label + value)
    let priceContainer = priceElement.parentElement;

    // Create the badge
    const badge = doc.createElement('div');
    badge.id = 'bta-availability-badge';
    badge.innerHTML = `
      <span style="display:flex;align-items:center;justify-content:center;gap:8px;">
        <span style="color:#22c55e;font-weight:bold;">✓</span>
        <span>Real-time availability loaded</span>
        <span id="bta-badge-info-icon" style="
          display:inline-flex;align-items:center;justify-content:center;
          width:15px;height:15px;border-radius:50%;
          background:transparent;border:1.5px solid #BD9B60;color:#BD9B60;
          font-size:10px;font-weight:700;font-style:normal;
          cursor:pointer;flex-shrink:0;user-select:none;
          font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
          transition:border-color 0.15s ease,color 0.15s ease;
        ">i</span>
      </span>
    `;
    badge.style.cssText = `
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      margin: 0 0 12px 0;
      padding: 10px 14px;
      border-radius: 6px;
      text-align: center;
      font-size: 14px;
      line-height: 1.4;
      background: #222222;
      border: 1px solid #BD9B60;
      color: #EAEAEA;
      cursor: default;
      position: relative;
    `;

    // Inject badge + tooltip styles if not already present
    if (!doc.getElementById('bta-badge-styles')) {
      const style = doc.createElement('style');
      style.id = 'bta-badge-styles';
      style.textContent = `
        @media (max-width: 480px) {
          #bta-availability-badge {
            font-size: 12px !important;
            padding: 8px 10px !important;
            margin-bottom: 8px !important;
          }
        }
        #bta-availability-tooltip {
          position: fixed;
          background: #2e2e2e;
          color: #EAEAEA;
          border: 1px solid #BD9B60;
          border-radius: 6px;
          padding: 10px 14px;
          font-size: 13px;
          line-height: 1.5;
          max-width: 260px;
          text-align: center;
          z-index: 99999;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.2s ease;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        }
        #bta-availability-tooltip.visible {
          opacity: 1;
        }
        #bta-availability-tooltip::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 6px solid transparent;
          border-top-color: #BD9B60;
        }
      `;
      doc.head.appendChild(style);
    }

    // Create tooltip in MAIN document so it never gets clipped by the iframe
    const mainDoc = window.document;
    let mainTooltip = mainDoc.getElementById('bta-availability-tooltip-daycare');
    if (!mainTooltip) {
      if (!mainDoc.getElementById('bta-badge-tooltip-main-styles')) {
        const mainStyle = mainDoc.createElement('style');
        mainStyle.id = 'bta-badge-tooltip-main-styles';
        mainStyle.textContent = `
          #bta-availability-tooltip-daycare {
            position: fixed;
            background: #2e2e2e;
            color: #EAEAEA;
            border: 1px solid #BD9B60;
            border-radius: 6px;
            padding: 10px 14px;
            font-size: 13px;
            line-height: 1.5;
            max-width: 260px;
            text-align: center;
            z-index: 99999;
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.2s ease;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            box-shadow: 0 4px 12px rgba(0,0,0,0.4);
          }
          #bta-availability-tooltip-daycare.visible { opacity: 1; }
        `;
        mainDoc.head.appendChild(mainStyle);
      }
      mainTooltip = mainDoc.createElement('div');
      mainTooltip.id = 'bta-availability-tooltip-daycare';
      mainTooltip.textContent = 'Live availability data successfully loaded.';
      mainDoc.body.appendChild(mainTooltip);
    }

    // Find the iframe to calculate offset for positioning
    function positionMainTooltip(triggerEl) {
      const iframes = mainDoc.querySelectorAll('iframe');
      let iframeRect = { top: 0, left: 0 };
      for (const iframe of iframes) {
        try {
          if (iframe.contentDocument && iframe.contentDocument === doc) {
            iframeRect = iframe.getBoundingClientRect();
            break;
          }
        } catch (e) { /* cross-origin */ }
      }
      const rect = triggerEl.getBoundingClientRect();
      const absTop = iframeRect.top + rect.top;
      const absLeft = iframeRect.left + rect.left;
      const viewportWidth = mainDoc.documentElement.clientWidth;
      const tooltipWidth = 260;
      let left = absLeft + rect.width / 2;
      if (left - tooltipWidth / 2 < 8) left = tooltipWidth / 2 + 8;
      if (left + tooltipWidth / 2 > viewportWidth - 8) left = viewportWidth - tooltipWidth / 2 - 8;
      mainTooltip.style.left = left + 'px';
      mainTooltip.style.top = absTop + 'px';
      mainTooltip.style.transform = 'translate(-50%, calc(-100% - 10px))';
    }

    const isMobile = 'ontouchstart' in window;
    let mobileTooltipVisible = false;
    let mobileDismissTimeout = null;

    function getInfoIcon() {
      return doc.getElementById('bta-badge-info-icon') || badge;
    }

    if (isMobile) {
      badge.addEventListener('click', (e) => {
        const infoIcon = getInfoIcon();
        if (e.target !== infoIcon && infoIcon !== badge) return;
        e.stopPropagation();
        if (mobileTooltipVisible) {
          mainTooltip.classList.remove('visible');
          mobileTooltipVisible = false;
          clearTimeout(mobileDismissTimeout);
        } else {
          positionMainTooltip(e.target);
          mainTooltip.classList.add('visible');
          mobileTooltipVisible = true;
          mobileDismissTimeout = setTimeout(() => {
            mainTooltip.classList.remove('visible');
            mobileTooltipVisible = false;
          }, 5000);
        }
      });
      doc.addEventListener('click', (e) => {
        if (mobileTooltipVisible && !badge.contains(e.target)) {
          mainTooltip.classList.remove('visible');
          mobileTooltipVisible = false;
          clearTimeout(mobileDismissTimeout);
        }
      });
    } else {
      const infoIcon = getInfoIcon();
      infoIcon.addEventListener('mouseenter', () => {
        positionMainTooltip(infoIcon);
        mainTooltip.classList.add('visible');
      });
      infoIcon.addEventListener('mouseleave', () => {
        mainTooltip.classList.remove('visible');
      });
      badge.addEventListener('mouseenter', () => {
        positionMainTooltip(badge);
        mainTooltip.classList.add('visible');
      });
      badge.addEventListener('mouseleave', () => {
        mainTooltip.classList.remove('visible');
      });
    }

    // Insert before the price container
    if (priceContainer && priceContainer.parentElement) {
      priceContainer.parentElement.insertBefore(badge, priceContainer);
    }
  }

  // ============================================================================
  // WAITLIST BUTTON - Always visible inside the BTA widget
  // ============================================================================
  /**
   * Injects an outlined "Dates Unavailable? Join Waitlist" button at the bottom
   * of the BTA widget (after the price container). Idempotent.
   */
  function injectWaitlistButton(doc) {
    if (!doc) return;

    // Skip if already injected
    if (doc.getElementById('bta-waitlist-button')) return;

    // Find the price element using same multi-selector fallback as the badge
    let priceElement = findPriceElement(doc);
    if (!priceElement) {
      const allSpans = doc.querySelectorAll('span');
      for (const span of allSpans) {
        if (span.textContent.includes('$') && span.textContent.match(/\d/)) {
          const prevText = span.previousSibling?.textContent || span.parentElement?.textContent || '';
          if (prevText.toLowerCase().includes('price')) {
            priceElement = span;
            break;
          }
        }
      }
    }
    if (!priceElement) return;

    // Walk up to the price row/wrapper (same logic as the badge)
    const priceContainer = priceElement.parentElement;
    if (!priceContainer || !priceContainer.parentElement) return;

    // Build the button as an anchor (opens new tab, no JS needed)
    const button = doc.createElement('a');
    button.id = 'bta-waitlist-button';
    button.href = WAITLIST_URL;
    button.target = '_blank';
    button.rel = 'noopener noreferrer';
    button.textContent = 'Dates Unavailable? Join Waitlist';
    button.style.cssText = `
      display: block;
      box-sizing: border-box;
      width: 100%;
      margin: 14px 0 4px 0;
      padding: 10px 14px;
      border: 1.5px solid #BD9B60;
      border-radius: 6px;
      background: transparent;
      color: #BD9B60;
      text-align: center;
      text-decoration: none;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.4;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    `;

    // Inject hover/responsive styles once per document
    if (!doc.getElementById('bta-waitlist-button-styles')) {
      const style = doc.createElement('style');
      style.id = 'bta-waitlist-button-styles';
      style.textContent = `
        #bta-waitlist-button:hover,
        #bta-waitlist-button:focus {
          background: #BD9B60 !important;
          color: #222222 !important;
          outline: none;
        }
        @media (max-width: 480px) {
          #bta-waitlist-button {
            font-size: 12px !important;
            padding: 8px 10px !important;
            margin-top: 10px !important;
          }
        }
      `;
      doc.head.appendChild(style);
    }

    // Insert AFTER the price container (i.e. at the very bottom of the widget)
    priceContainer.parentElement.insertBefore(button, priceContainer.nextSibling);
  }

  function injectDatesLabel(doc) {
    // Skip if SHOW_DATE_LIST is false
    if (!SHOW_DATE_LIST) {
      return;
    }

    // Check if label already exists
    if (doc.getElementById('bta-dates-label')) {
      return;
    }

    // Find the first booking entry to insert the label before it
    const bookingEntries = findBookingEntries(doc);
    if (bookingEntries.length === 0) {
      // No bookings yet, try again later
      return;
    }

    // Get the first booking element
    const firstBooking = bookingEntries[0].bookingElement;
    
    // Find the parent container that holds all bookings
    let bookingContainer = firstBooking.parentElement;
    
    // Create the label
    const label = doc.createElement('div');
    label.id = 'bta-dates-label';
    label.textContent = 'Dates selected:';
    label.style.cssText = `
      font-weight: bold; 
      font-size: 16px; 
      margin-bottom: 10px;
      color: #ffffff !important;
    `;
    
    // Add mobile-responsive styles if not already present
    if (!doc.getElementById('bta-label-styles')) {
      const style = doc.createElement('style');
      style.id = 'bta-label-styles';
      style.textContent = `
        @media (max-width: 480px) {
          #bta-dates-label {
            font-size: 14px !important;
            margin-bottom: 8px !important;
          }
        }
      `;
      doc.head.appendChild(style);
    }
    
    // Insert the label before the first booking
    bookingContainer.insertBefore(label, firstBooking);
    
    // console.log('%c[BTA Main] ✓ "Dates selected:" label injected', 'color: #10b981; font-weight: bold');
    
    if (DIAGNOSTIC_MODE) {
      logDiagnostic('Label added: "Dates selected:"', 'success');
    }
  }

  // ============================================================================
  // TOOLTIP FOR ADJACENT MONTH DATES
  // ============================================================================
  
  let currentTooltip = null;
  let tooltipTimeout = null;

  function isDateInPastOrToday(dateNumber, monthName, year) {
    // Create date object for the clicked date at midnight
    const clickedDate = new Date(year, getMonthIndex(monthName), dateNumber);
    clickedDate.setHours(0, 0, 0, 0);
    
    // Get today's date at midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Return true if clicked date is today or in the past
    return clickedDate <= today;
  }

  function showMonthTooltip(button, targetMonthName, isPreviousMonth) {
    // Remove any existing tooltip
    hideMonthTooltip();

    const doc = state.iframeDoc;
    if (!doc) return;

    // Determine arrow direction based on whether it's previous or next month
    const arrowDirection = isPreviousMonth ? 'left' : 'right';

    // Create tooltip element in the iframe document
    const tooltip = doc.createElement('div');
    tooltip.className = 'bta-month-tooltip';
    tooltip.textContent = `Use the ${arrowDirection} arrow above to navigate to ${targetMonthName} to book dates in ${targetMonthName}.`;
    
    // Get button position relative to iframe
    const rect = button.getBoundingClientRect();
    
    // Calculate position (center above the button)
    let left = rect.left + (rect.width / 2);
    let top = rect.top - 10; // 10px above button
    
    // Get iframe dimensions to check boundaries
    const iframeWidth = doc.documentElement.clientWidth || doc.body.clientWidth;
    
    // Adjust for boundaries - if too close to edges, reposition
    const tooltipEstimatedWidth = 280;
    if (left - (tooltipEstimatedWidth / 2) < 10) {
      left = tooltipEstimatedWidth / 2 + 10;
    } else if (left + (tooltipEstimatedWidth / 2) > iframeWidth - 10) {
      left = iframeWidth - (tooltipEstimatedWidth / 2) - 10;
    }
    
    tooltip.style.left = left + 'px';
    tooltip.style.top = top + 'px';
    tooltip.style.transform = 'translate(-50%, -100%)';
    
    doc.body.appendChild(tooltip);
    currentTooltip = tooltip;

    // console.log('[BTA Main] Tooltip shown for month:', targetMonthName);

    // Auto-hide after 3 seconds on mobile (for tap interactions)
    if ('ontouchstart' in window) {
      tooltipTimeout = setTimeout(hideMonthTooltip, 3000);
    }
  }

  function hideMonthTooltip() {
    if (currentTooltip) {
      currentTooltip.remove();
      currentTooltip = null;
    }
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
      tooltipTimeout = null;
    }
  }

  // ============================================================================
  // BOOK NOW LOADING OVERLAY
  // ============================================================================
  
  let bookNowLoadingOverlay = null;

  function showBookNowLoadingOverlay() {
    if (bookNowLoadingOverlay) {
      // console.log('[BTA Main] ⚠️ Loading overlay already exists, not creating duplicate');
      return;
    }
    
    // console.log('[BTA Main] ✅ CREATING Book Now loading overlay NOW!');
    // console.log('[BTA Main] document.body exists:', !!document.body);

    bookNowLoadingOverlay = document.createElement('div');
    bookNowLoadingOverlay.id = 'bta-book-now-loading';
    bookNowLoadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(34, 34, 34, 0.97);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      animation: btaFadeIn 0.3s ease-out;
    `;
    
    bookNowLoadingOverlay.innerHTML = `
      <div style="position: relative; overflow: hidden; text-align: center; padding: 40px 36px 36px; width: calc(100% - 32px); max-width: 480px; box-sizing: border-box; background: #222222; border: 1px solid #BD9B60; border-radius: 14px; box-shadow: 0 16px 48px rgba(0,0,0,0.5);">
        <div style="position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, #7a6236, #BD9B60, #f1dea6, #BD9B60, #7a6236);"></div>
        <div style="display: inline-block; width: 60px; height: 60px; border: 5px solid #464646; border-top: 5px solid #BD9B60;
          border-radius: 50%; animation: btaSpin 1s linear infinite; margin-bottom: 24px;"></div>
        <div style="font-size: 26px; font-weight: 600; color: #FFFFFF; margin-bottom: 12px; animation: btaPulse 2s ease-in-out infinite;">
          🐾 Reviewing Your Selected Dates
        </div>
        <div style="font-size: 16px; color: #EAEAEA; line-height: 1.6;">
          Please wait while we review the daycare dates selected.
          This may take a moment depending on the number of dates selected...
        </div>
      </div>
      <style>
        @keyframes btaSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes btaPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
        }
        @keyframes btaFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      </style>
    `;
    
    // IMPORTANT: Append to parent window (not iframe) so overlay persists during form submission
    document.body.appendChild(bookNowLoadingOverlay);
    document.body.style.overflow = 'hidden';
    // console.log('[BTA Main] 🎉 Loading overlay successfully appended to body!');
    // console.log('[BTA Main] Overlay element:', bookNowLoadingOverlay);
  }

  // ==========================================================================
  // MONTH JUMP DROPDOWN
  // Replaces the react-day-picker month label with a native <select> so users
  // can jump straight to any of the next 12 months instead of clicking the
  // arrows one month at a time. Purely additive. The original label is hidden
  // (not removed) and kept as the first child of .rdp-caption so the existing
  // getCurrentMonth() document scan still resolves the displayed month before
  // it ever reaches the <option> elements.
  // ==========================================================================

  function getMonthDropdownOptions() {
    const options = [];
    const now = new Date();
    let y = now.getFullYear();
    let m = now.getMonth(); // 0-11
    for (let i = 0; i < 12; i++) {
      options.push({
        monthIndex: m,
        year: y,
        label: getMonthName(m) + ' ' + y
      });
      m++;
      if (m > 11) { m = 0; y++; }
    }
    return options;
  }

  function syncMonthDropdown(doc) {
    if (!doc) return;
    const select = doc.getElementById('bta-month-jump');
    if (!select) return;

    const current = getCurrentMonth(doc);
    if (!current) return;

    const monthIndex = getMonthIndex(current.month);
    const year = parseInt(current.year, 10);
    if (monthIndex === -1 || isNaN(year)) return;

    const value = year + '-' + monthIndex;
    let hasOption = false;
    for (let i = 0; i < select.options.length; i++) {
      if (select.options[i].value === value) { hasOption = true; break; }
    }
    if (hasOption && select.value !== value) {
      select.value = value;
    }
  }

  // Hybrid navigation. react-day-picker owns its month state, so the only way to
  // move it is to click the < / > arrows. When no dates are selected, BTA's
  // per-navigation plus-button reset is a no-op, so we step quickly. When dates
  // ARE selected, we pace each click so the navigation cascade settles first and
  // the plus-button resets never stack.
  function navigateCalendarToMonth(doc, targetMonthIndex, targetYear) {
    if (!doc) return;

    const targetOrdinal = targetYear * 12 + targetMonthIndex;
    const select = doc.getElementById('bta-month-jump');

    if (doc._btaMonthNavigating) {
      log('navigateCalendarToMonth: navigation already in progress');
      return;
    }
    doc._btaMonthNavigating = true;
    if (select) select.disabled = true;

    let settleDelay;
    try {
      settleDelay = (state && state.selectedDates && state.selectedDates.size > 0) ? 700 : 0;
    } catch (e) {
      settleDelay = 700; // be safe if state is unavailable
    }

    const MAX_STEPS = 13;
    let steps = 0;

    function readState() {
      const cur = getCurrentMonth(doc);
      if (!cur) return null;
      const mi = getMonthIndex(cur.month);
      const y = parseInt(cur.year, 10);
      if (mi === -1 || isNaN(y)) return null;
      return { ordinal: y * 12 + mi, text: cur.month + ' ' + cur.year };
    }

    function finish() {
      doc._btaMonthNavigating = false;
      if (select) select.disabled = false;
      syncMonthDropdown(doc);
    }

    function step() {
      const cur = readState();
      if (cur === null) { finish(); return; }
      if (cur.ordinal === targetOrdinal) { finish(); return; }
      if (steps >= MAX_STEPS) {
        log('navigateCalendarToMonth: hit MAX_STEPS without reaching target');
        finish();
        return;
      }
      steps++;

      const goForward = cur.ordinal < targetOrdinal;
      const btn = goForward
        ? doc.querySelector('button[name="next-month"], button.rdp-nav_button_next')
        : doc.querySelector('button[name="previous-month"], button.rdp-nav_button_previous');
      if (!btn || btn.disabled) {
        log('navigateCalendarToMonth: nav button missing or disabled');
        finish();
        return;
      }

      const beforeText = cur.text;
      btn.click();

      // Wait for the caption to change (react re-render), then pause settleDelay
      // so BTA's month-navigation cascade can finish before the next click.
      let waited = 0;
      const POLL = 40;
      const MAX_WAIT = 1500;
      const poll = function() {
        if (!doc._btaMonthNavigating) return; // safety: aborted elsewhere
        const now = readState();
        if (now && now.text !== beforeText) {
          if (settleDelay > 0) {
            setTimeout(step, settleDelay);
          } else {
            step();
          }
          return;
        }
        waited += POLL;
        if (waited >= MAX_WAIT) {
          log('navigateCalendarToMonth: timed out waiting for month change');
          finish();
          return;
        }
        setTimeout(poll, POLL);
      };
      setTimeout(poll, POLL);
    }

    step();
  }

  // Keep the dropdown alive across re-renders: re-inject if it gets wiped, keep
  // the native label hidden, and sync the selected value when the user navigates
  // with the arrows. Attributes are not observed, so hiding the label / setting
  // the value never re-triggers this.
  function setupMonthDropdownObserver(doc) {
    if (!doc) return;
    if (doc._btaMonthDropdownObserver) return; // already observing this document

    const target = doc.querySelector('.rdp') || doc.querySelector('.rdp-caption');
    if (!target) return;

    const observer = new MutationObserver(function() {
      if (doc._btaMonthNavigating) return; // don't fight our own arrow-stepping

      let caption = doc.querySelector('.rdp-caption');
      if (!caption) {
        const lbl = doc.querySelector('.rdp-caption_label');
        if (lbl) caption = lbl.parentElement;
      }
      if (!caption) return;

      const select = doc.getElementById('bta-month-jump');
      if (!select || !caption.contains(select)) {
        injectMonthDropdown(doc);
      } else {
        const label = caption.querySelector('.rdp-caption_label');
        if (label && label.style.display !== 'none') {
          label.style.display = 'none';
        }
        syncMonthDropdown(doc);
      }
    });

    observer.observe(target, { childList: true, subtree: true });
    doc._btaMonthDropdownObserver = observer;
    log('\u2713 Month dropdown observer set up');
  }

  function injectMonthDropdown(doc) {
    if (!doc) return;

    let caption = doc.querySelector('.rdp-caption');
    if (!caption) {
      const lbl = doc.querySelector('.rdp-caption_label');
      if (lbl) caption = lbl.parentElement;
    }
    if (!caption) {
      log('injectMonthDropdown: no caption found');
      return;
    }

    const label = caption.querySelector('.rdp-caption_label');

    // Idempotent: if the select already exists in this caption, resync + ensure
    // the observer is running.
    let select = doc.getElementById('bta-month-jump');
    if (select && caption.contains(select)) {
      if (label && label.style.display !== 'none') label.style.display = 'none';
      syncMonthDropdown(doc);
      setupMonthDropdownObserver(doc);
      return;
    }

    // Inject styles once per document.
    if (!doc.getElementById('bta-month-jump-styles')) {
      const style = doc.createElement('style');
      style.id = 'bta-month-jump-styles';
      style.textContent = `
        #bta-month-jump {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: #FFFFFF;
          background-color: transparent;
          border: none;
          border-radius: 6px;
          padding: 6px 30px 6px 10px;
          margin: 0;
          cursor: pointer;
          line-height: 1.3;
          max-width: 100%;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23BD9B60' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 10px center;
        }
        #bta-month-jump:hover {
          background-color: #BD9B60;
        }
        #bta-month-jump:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(189,155,96,0.35);
        }
        #bta-month-jump:disabled {
          opacity: 0.6;
          cursor: default;
        }
        #bta-month-jump option {
          background-color: #FFFFFF;
          color: #222222;
        }
        @media (max-width: 480px) {
          #bta-month-jump {
            font-size: 14px;
            padding: 5px 26px 5px 8px;
          }
        }
      `;
      doc.head.appendChild(style);
    }

    // Hide the native label but keep it in the DOM as the first caption child
    // (getCurrentMonth reads it).
    if (label) label.style.display = 'none';

    // Build the select.
    select = doc.createElement('select');
    select.id = 'bta-month-jump';
    select.setAttribute('aria-label', 'Jump to month');

    const opts = getMonthDropdownOptions();
    for (let i = 0; i < opts.length; i++) {
      const o = opts[i];
      const optEl = doc.createElement('option');
      optEl.value = o.year + '-' + o.monthIndex;
      optEl.textContent = o.label;
      select.appendChild(optEl);
    }

    // Insert where the label sat: after the (hidden) label, before .rdp-nav.
    const nav = caption.querySelector('.rdp-nav');
    if (label && label.nextSibling) {
      caption.insertBefore(select, label.nextSibling);
    } else if (nav) {
      caption.insertBefore(select, nav);
    } else {
      caption.appendChild(select);
    }

    // Reflect the currently displayed month.
    syncMonthDropdown(doc);

    // Navigate on change.
    select.addEventListener('change', function() {
      const parts = select.value.split('-');
      const targetYear = parseInt(parts[0], 10);
      const targetMonthIndex = parseInt(parts[1], 10);
      if (isNaN(targetYear) || isNaN(targetMonthIndex)) return;
      navigateCalendarToMonth(doc, targetMonthIndex, targetYear);
    });

    setupMonthDropdownObserver(doc);

    log('\u2713 Month jump dropdown injected');
  }

  function installEventListeners(doc) {
    // Click listener - detects Book Now button and triggers overlay
    doc.addEventListener('click', (e) => {
      // Check if this is a button (any type)
      const clickedButton = e.target.closest('button');
      if (clickedButton) {
        const btnText = clickedButton.textContent?.toLowerCase() || '';
        
        // Check if it's the "Book Now" button (regardless of type)
        if (btnText.includes('book') && btnText.includes('now')) {
          // Generate a unique session ID for this booking batch if not already set
          if (!flexState.sessionId) {
            flexState.sessionId = 'kcb_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
            log(`Flex: generated sessionId=${flexState.sessionId}`);
          }
          // Only show overlay immediately if Flex modal has already been shown.
          // If not, the fetch interceptor will show it after the modal resolves.
          if (flexState.shown) {
            showBookNowLoadingOverlay();
          }
        }
      }
      
      handleDateClick(e);
      handleMonthNavigation(e);
    }, true);
    
    // Mousedown listener - catches clicks even earlier
    doc.addEventListener('mousedown', (e) => {
      handleDateClick(e);
    }, true);

    // Tooltip handlers for dates from other months
    doc.addEventListener('mouseover', (e) => {
      const button = e.target.closest('button.rdp-day');
      if (!button) return;

      const td = button.closest('td');
      if (!td) return;

      const dateText = button.textContent.trim();
      const dateNumber = parseInt(dateText, 10);
      if (isNaN(dateNumber)) return;

      // Determine if this is a date from another month
      const tr = td.parentElement;
      const tbody = tr.parentElement;
      const clickedRow = Array.from(tbody.children).indexOf(tr);
      const totalRows = tbody.children.length;

      const currentMonth = getCurrentMonth(state.iframeDoc);
      if (!currentMonth) return;

      let targetMonth = null;
      let targetYear = currentMonth.year;
      let isPreviousMonth = false;
      let isFromAnotherMonth = false;

      // Check if this is a previous month date (high numbers in first rows)
      if (dateNumber >= 22 && clickedRow <= 1) {
        const prevMonthDate = new Date(currentMonth.year, getMonthIndex(currentMonth.month), 0);
        targetMonth = getMonthName(prevMonthDate.getMonth());
        targetYear = prevMonthDate.getFullYear().toString();
        isPreviousMonth = true;
        isFromAnotherMonth = true;
      } 
      // Check if this is a next month date (low numbers in last row)
      else if (dateNumber <= 7 && clickedRow >= totalRows - 1) {
        const nextMonthDate = new Date(currentMonth.year, getMonthIndex(currentMonth.month) + 1, 1);
        targetMonth = getMonthName(nextMonthDate.getMonth());
        targetYear = nextMonthDate.getFullYear().toString();
        isPreviousMonth = false;
        isFromAnotherMonth = true;
      } else {
        // This is a date from the current month
        targetMonth = currentMonth.month;
        targetYear = currentMonth.year;
        isFromAnotherMonth = false;
      }

      // Handle dates from another month
      if (isFromAnotherMonth && targetMonth !== currentMonth.month) {
        // Check if this date is in the past or today
        if (isDateInPastOrToday(dateNumber, targetMonth, targetYear)) {
          // Add class for not-allowed cursor, don't show tooltip
          button.classList.add('bta-past-date');
        } else {
          // Future date - show tooltip
          button.classList.remove('bta-past-date');
          showMonthTooltip(button, targetMonth, isPreviousMonth);
        }
      } 
      // Handle dates from current month that are past/today
      else if (!isFromAnotherMonth) {
        if (isDateInPastOrToday(dateNumber, targetMonth, targetYear)) {
          // Add class for not-allowed cursor for past dates in current month
          button.classList.add('bta-past-date');
        } else {
          // Future date in current month - remove the class
          button.classList.remove('bta-past-date');
        }
      }
    }, true);

    doc.addEventListener('mouseout', (e) => {
      const button = e.target.closest('button.rdp-day');
      if (button) {
        hideMonthTooltip();
      }
    }, true);

    // Touch support for mobile
    doc.addEventListener('touchstart', (e) => {
      const button = e.target.closest('button.rdp-day');
      if (!button) {
        hideMonthTooltip();
        return;
      }

      const td = button.closest('td');
      if (!td) return;

      const dateText = button.textContent.trim();
      const dateNumber = parseInt(dateText, 10);
      if (isNaN(dateNumber)) return;

      const tr = td.parentElement;
      const tbody = tr.parentElement;
      const clickedRow = Array.from(tbody.children).indexOf(tr);
      const totalRows = tbody.children.length;

      const currentMonth = getCurrentMonth(state.iframeDoc);
      if (!currentMonth) return;

      let targetMonth = null;
      let targetYear = currentMonth.year;
      let isPreviousMonth = false;
      let isFromAnotherMonth = false;

      if (dateNumber >= 22 && clickedRow <= 1) {
        const prevMonthDate = new Date(currentMonth.year, getMonthIndex(currentMonth.month), 0);
        targetMonth = getMonthName(prevMonthDate.getMonth());
        targetYear = prevMonthDate.getFullYear().toString();
        isPreviousMonth = true;
        isFromAnotherMonth = true;
      } else if (dateNumber <= 7 && clickedRow >= totalRows - 1) {
        const nextMonthDate = new Date(currentMonth.year, getMonthIndex(currentMonth.month) + 1, 1);
        targetMonth = getMonthName(nextMonthDate.getMonth());
        targetYear = nextMonthDate.getFullYear().toString();
        isPreviousMonth = false;
        isFromAnotherMonth = true;
      } else {
        // This is a date from the current month
        targetMonth = currentMonth.month;
        targetYear = currentMonth.year;
        isFromAnotherMonth = false;
      }

      // Handle dates from another month
      if (isFromAnotherMonth && targetMonth !== currentMonth.month) {
        // Check if this date is in the past or today
        if (isDateInPastOrToday(dateNumber, targetMonth, targetYear)) {
          // Add class for not-allowed cursor, don't show tooltip
          button.classList.add('bta-past-date');
        } else {
          // Future date - show tooltip
          button.classList.remove('bta-past-date');
          showMonthTooltip(button, targetMonth, isPreviousMonth);
        }
        // Prevent the click from going through on touch
        e.preventDefault();
      } 
      // Handle dates from current month that are past/today
      else if (!isFromAnotherMonth) {
        if (isDateInPastOrToday(dateNumber, targetMonth, targetYear)) {
          // Add class for not-allowed cursor for past dates in current month
          button.classList.add('bta-past-date');
          // Also prevent click
          e.preventDefault();
        } else {
          // Future date in current month - remove the class
          button.classList.remove('bta-past-date');
        }
      }
    }, true);

    // Submit listener - catches Book Now button click
    doc.addEventListener('submit', (e) => {
      recordBehavior('FORM_SUBMIT', '🎯🎯🎯 FORM SUBMIT EVENT DETECTED 🎯🎯🎯', {
        action: e.target.action,
        method: e.target.method,
        formId: e.target.id,
        formClasses: Array.from(e.target.classList || [])
      });
      // console.log('[BTA Main] 🎯 Form submit detected - showing loading overlay');
      showBookNowLoadingOverlay();
      // Don't prevent default - let the form submit
    }, true);

    recordBehavior('STATE_CHANGE', '✅ All event listeners installed successfully');
    logDiagnostic('Event listeners installed (mousedown + click + tooltips + submit, capture phase)', 'success');
    // console.log('%c[BTA Main] ✓ Event listeners installed', 'color: #10b981; font-weight: bold');
  }

  function init() {
    if (!window.location.pathname.includes(state.config.productPath)) {
      // console.log('[BTA Main] Not on daycare page');
      return;
    }

    // console.log('[BTA Main] Initializing...');
    
    if (DIAGNOSTIC_MODE) {
      // Setup auto-save for diagnostic logs
      setupAutoSave();
      createDiagnosticUI();
      recordBehavior('STATE_CHANGE', 'Script initializing on product page', { path: window.location.pathname });
    }
    
    // Add listeners on main page too (outside iframe) - only when diagnostic mode is on
    if (DIAGNOSTIC_MODE) {
      document.addEventListener('click', (e) => {
        recordBehavior('BUTTON_CLICK', 'Click detected on MAIN PAGE (outside iframe)', {
          tagName: e.target.tagName?.toLowerCase(),
          type: e.target.type,
          text: e.target.textContent?.trim().substring(0, 50),
          classList: Array.from(e.target.classList || []),
          id: e.target.id
        });
      }, true);
      
      document.addEventListener('submit', (e) => {
        recordBehavior('FORM_SUBMIT', '🎯 FORM SUBMIT on MAIN PAGE (outside iframe)', {
          action: e.target.action,
          method: e.target.method
        });
      }, true);
    }

    let attempts = 0;
    const maxAttempts = 120;
    
    const tryInit = () => {
      attempts++;
      
      const doc = getIframeDoc();
      if (!doc) {
        if (attempts < maxAttempts) {
          setTimeout(tryInit, 500);
        }
        return;
      }

      recordBehavior('STATE_CHANGE', 'Iframe document found', { attempts: attempts });

      // Inject hide CSS immediately — as soon as iframe doc is accessible,
      // before waiting for the calendar. This prevents the timeslot and date
      // list from ever being visible to the customer.
      injectHideCSS(doc);

      // Wait for calendar
      const calendar = doc.querySelector('table, .rdp, [class*="calendar"]');
      if (!calendar && attempts < maxAttempts) {
        if (attempts % 10 === 0) {
          recordBehavior('STATE_CHANGE', `Waiting for calendar...`, { attempts: attempts, maxAttempts: maxAttempts });
        }
        setTimeout(tryInit, 500);
        return;
      }

      if (!calendar) {
        recordBehavior('ERROR', 'Calendar not found after timeout');
        return;
      }

      recordBehavior('STATE_CHANGE', 'Calendar found and loaded');

      // Setup mutation observer to watch for timeslot field creation
      setupMutationObserver();

      // Inject CSS to hide timeslot field and plus button
      injectHideCSS(doc);

      // Inject real-time availability badge below calendar, above price
      injectAvailabilityBadge(doc);

      // Inject waitlist button at the bottom of the widget
      injectWaitlistButton(doc);

      // Replace the month label with a jump-to-month dropdown
      injectMonthDropdown(doc);

      // Install event listeners
      installEventListeners(doc);

      // Initial scan to detect any existing bookings on page load
      scanAndUpdate();

      recordBehavior('STATE_CHANGE', 'System ready - Event-driven mode active');
    };
    
    // Fast early CSS injection — poll every 100ms until calendar is loaded
    // Reinjects on every poll to survive BTA document replacement during init
    let earlyPollActive = true;
    function earlyInjectCSS() {
      if (!earlyPollActive) return;
      const doc = getIframeDoc();
      if (doc && doc.head) {
        // Remove stale style tag if doc changed (style tag gone after document replacement)
        const existing = doc.getElementById('bta-hide-elements-style');
        if (!existing) {
          injectHideCSS(doc);
        }
        // Stop polling once calendar is fully loaded (tryInit will take over)
        if (doc.querySelector('table, .rdp, [class*="calendar"]')) {
          earlyPollActive = false;
          return;
        }
      }
      setTimeout(earlyInjectCSS, 100);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        earlyInjectCSS();
        setTimeout(tryInit, 2000);
      });
    } else {
      earlyInjectCSS();
      setTimeout(tryInit, 2000);
    }
  }

  init();

})();